import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Switch } from "../../../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Alert, AlertDescription } from "../../../ui/alert";
import { Badge } from "../../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Textarea } from "../../../ui/textarea";
import { Tooltip, TooltipTrigger, TooltipContent } from "../../../ui/tooltip";
import { AlertCircle, AlertOctagon, Loader2, AlertTriangle, CheckCircle, XCircle, ChevronLeft, ChevronRight, GitMerge, PauseCircle, Info } from "lucide-react";
import { cn } from "../../../utils/UnderwritingEngineV2/utils";
import axios from "axios";
import UserService from "../../../services/UserService";
import { rolesAtOrAbove } from "../../../utils/common/productRoles";
import { mapUrlToProductId } from "../../../utils/common/mapUrlToProduct";
import { logWorkflowAction } from "../../../utils/workflowLogger";
import {
  BANK_STATEMENT_FIELDS,
  LOAN_AMOUNT_MAX,
  bankStatementCriteria,
  corpGridFields,
  isMicroTicket,
  yesNo,
} from "../../../utils/UnderwritingEngineV2/corpScoringGrid";
import Underwrite from "./Underwrite";
import IntroAnimation from "../../../components/IntroAnimation";
// @ts-ignore
import logo from "../../../assets/images/icons/logo-large.png";
// @ts-ignore
import splashlogo from "../../../assets/images/icons/splashlogo.png";

const serverConfig = require("../../../config/server.js");

// This is a large component - maintaining all business logic from original
// Converting Bootstrap components to shadcn/ui components
interface LoanCalculateProps {
  currentStep: "Loans" | "Application" | "Score" | "Review" | "Underwrite";
  setCurrentStep: (step: "Loans" | "Application" | "Score" | "Review" | "Underwrite") => void;
}

const FIELD_DISPLAY_LABELS: Record<string, string> = {
  applicationNumber: "Application Number",
  nameOfBusiness: "Name of Business",
  officeName: "Office Name",
  loanAmount: "Loan Amount",
  approvedAmount: "Approved Amount",
  assetType: "Asset Type",
  city: "City",
  avgHighCredit: "Average High Credit",
  highCredit: "High Credit",
  paydex: "Paydex",
  suits: "Suits",
  liens: "Liens",
  judgements: "Judgements",
  startYear: "Start Year",
  noOfTradeExperiences: "Number of Trade Lines",
  bankruptcy: "Bankruptcy",
  standardCode: "SIC Code",
  commercialCreditScore: "Commercial Credit Score",
  birthyear: "Birth Year",
  vantage: "Vantage",
  noOfSatisfactoryAccounts: "Number of Satisfactory Accounts",
  noOfInquiries: "Number of Inquiries",
  noOfTotalDelinquencies: "Was Delinquent",
  noOfDelinquencies: "Number of Delinquencies",
  udf_fico_average: "Average FICO Score",
  udf_fico_max: "Max FICO Score",
  udf_fico: "FICO Score",
  totalRevolvingBalance: "Total Revolving Balance",
  totalRevolvingAvailable: "Total Revolving Available",
  realStateBalance: "Real Estate Balance",
  pnMasterScore: "PN Master Score",
  udf_pass_score: "Pass Score",
  pnHighCredit: "High Credit",
  pnNoOfTradeExperiences: "Number of Trade Lines",
  noOfGuarantors: "Personal Guarantors",
  noOfCorpGuarantors: "Corporate Guarantors",
  timeInBusiness: "Time In Business",
  numberOfEmployees: "Number of Employees",
  yield: "Yield",
  ...Object.fromEntries(BANK_STATEMENT_FIELDS.map(({ field, label }) => [field, label])),
  usePGSwitch: "Personal Guarantor (PG)",
  pgTimeInBusiness: "Time in Business",
  pgFicoScore: "FICO Score",
  pgStrongPaymentHistorySwitch: "Strong Payment History",
  pgAcceptableCreditHistorySwitch: "Acceptable Payment History",
  pgStrongComparibleCreditSwitch: "Strong Comparable Credit",
  pgAcceptableComparibleCreditSwitch: "Acceptable Comparable Credit",
  pgPreferredCollateralSwitch: "Preferred Collateral",
  pgSoftCollateralSwitch: "Soft Collateral",
  pgIntangiblesSwitch: "Intangibles",
  pgIntangiblesCustomPoints: "Intangibles Custom Points",
  pgOverrideSwitch: "Override",
  pgOverrideCustomPoints: "Override Custom Points",
  pgOverrideText: "Override Text",
  useCorpSwitch: "Corp Details",
  corpTimeInBusiness: "Time in Business",
  corpNumberOfEmployees: "Number of Employees",
  corpHighCredit: "High Credit",
  corpPaydex: "Paydex",
  corpTradeLines: "Trade Lines",
  suitsLiensJudgements: "Suits/Liens/Judgements",
  historyClearSwitch: "History Clear",
  uccFilingsSwitch: "UCC Filings",
  highRiskIndustrySwitch: "High Risk Industry",
  comparableLeaseSwitch: "Comparable Lease",
  preferredVendorSwitch: "Preferred Vendor",
  preferredVendorPoints: "Preferred Vendor Points",
  majorityEquitySingleOwnershipSwitch: "Majority Equity Single Ownership",
  preferredCollateralSwitch: "Preferred Collateral",
  softCollateralSwitch: "Soft Collateral",
  intangiblesSwitch: "Intangibles",
  corpIntangiblesCustomPoints: "Intangibles Custom Points",
  overrideSwitch: "Override",
  corpOverrideCustomPoints: "Override Custom Points",
  overrideText: "Override Text",
};

// Scoring-criteria descriptions shown via the info icon next to each switch's
// label — same wording as the underwriter reference table for these criteria.
const CRITERIA_INFO: Record<string, string> = {
  "Strong Payment History": "Paydex equal to or greater than 70 OR Multiple Lease Repayments on Paynet",
  "Acceptable Payment History": "Non-Derogatory Navitas Payment History",
  "Strong Comparable Credit": "2x Requested Amount in D&B, Paynet or any Loan (Except Mortgage) on CBR",
  "Acceptable Comparable Credit": "Similar Amount in any Bureau (Except Mortgage)",
  "Preferred Collateral": "Copiers, Computers, Telecom Hardware, Medical, Automotive or any Hard Asset",
  "Soft Collateral": "Security Systems, Software, Low Correlation New Technology",
  "Intangibles": "Fins, Resume, High Scores, Banks, Other",
  "Vendor Support": "Source of Transaction is Clear and Understood",
};

function CriteriaInfo({ label }: { label: keyof typeof CRITERIA_INFO }) {
  const text = CRITERIA_INFO[label];
  if (!text) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex h-4 w-4 items-center justify-center text-muted-foreground hover:text-amber focus-visible:text-amber"
          aria-label={`About ${label}`}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="text-xs">{text}</p>
      </TooltipContent>
    </Tooltip>
  );
}

const NON_NEGATIVE_FIELD_NAMES = new Set<string>([
  "applicationNumber",
  "loanAmount",
  "avgHighCredit",
  "highCredit",
  "startYear",
  "noOfTradeExperiences",
  "bankruptcy",
  "commercialCreditScore",
  "birthyear",
  "vantage",
  "noOfSatisfactoryAccounts",
  "noOfInquiries",
  "noOfTotalDelinquencies",
  "noOfDelinquencies",
  "udf_fico_average",
  "udf_fico_max",
  "udf_fico",
  "totalRevolvingBalance",
  "totalRevolvingAvailable",
  "realStateBalance",
  "pnMasterScore",
  "udf_pass_score",
  "pnHighCredit",
  "pnNoOfTradeExperiences",
  "noOfGuarantors",
  "noOfCorpGuarantors",
  "timeInBusiness",
  "numberOfEmployees",
  "yield",
  "pgTimeInBusiness",
  "pgFicoScore",
  "corpTimeInBusiness",
  "corpNumberOfEmployees",
  "corpHighCredit",
  "corpPaydex",
  "corpTradeLines",
  "optimismIndex",
  "gdpGrowthRate",
  "cpiIndex",
  "fedFundsRate",
  "industrialProductionIndex",
  "unemploymentRate",
  "cpiMoM",
  "cpiRoll3M",
  "gdpRoll3Q",
  "unemploymentRoll3M",
  "optimismRoll6M",
  "industrialProdChange6M",
  "covidFlag",
  "optimismIndexNormalized",
  "unemploymentRateNormalized",
  "gdpGrowthRateNormalized",
  "pgScore",
  "corpScore",
  "requiresPersonalGuarantor",
  "corpThreeOfFourCriteriaFailed",
  "corpOneOfTwoCriteriaFailed",
  "loanShortfallSupportFailed",
  "bankStatementCriteriaFailed",
  "pgIntangiblesCustomPoints",
  "pgOverrideCustomPoints",
  "corpIntangiblesCustomPoints",
  "corpOverrideCustomPoints",
  "preferredVendorPoints",
]);

const BUREAU_FIELDS = new Set([
  'udf_fico_average', 'udf_fico_max', 'udf_fico', 'pnMasterScore', 'udf_pass_score', 'paydex', 'commercialCreditScore'
]);

// UI fields whose change-reason is optional in the criteria modal (none today).
const LEASEWORKS_OPTIONAL_FIELDS = new Set<string>();

// athena_status vocabulary (canonical app state). Terminal states lock UE editing;
// hold states show the "back to Credit Review" exit. Refer is a separate flag.
const TERMINAL_ATHENA_STATUSES = new Set(["Approved", "Rejected", "Withdrawn", "Syndicate"]);
const HOLD_ATHENA_STATUSES = new Set(["Credit Hold", "Hold"]);
const isTerminalStatus = (s: string | null | undefined): boolean => !!s && TERMINAL_ATHENA_STATUSES.has(s);
const isHoldStatus = (s: string | null | undefined): boolean => !!s && HOLD_ATHENA_STATUSES.has(s);

const toNonNegativeValue = (fieldName: string, value: any) => {
  if (!NON_NEGATIVE_FIELD_NAMES.has(fieldName)) {
    return value;
  }
  if (value === "" || value === null || value === undefined) {
    return value;
  }
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }
  if (numericValue >= 0) {
    return value;
  }
  if (fieldName === "realStateBalance") {
    return typeof value === "string" ? "0" : 0;
  }
  return "";
};

const sanitizeNonNegativeFields = <T extends Record<string, any>>(data: T): T => {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, toNonNegativeValue(key, value)])
  ) as T;
};

const sanitizeDisplay = (value: any, mode: 0 | 1 = 0): any => {
  if (mode === 1) {
    if (value === null || value === undefined || value === "") return "0";
    const num = Number(value);
    return !Number.isNaN(num) && num < 0 ? "0" : value;
  }
  if (value === null || value === undefined || value === "") return value;
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num < 0 ? "" : value;
};

const toDisplayLabel = (fieldName: string) => {
  if (FIELD_DISPLAY_LABELS[fieldName]) {
    return FIELD_DISPLAY_LABELS[fieldName];
  }
  // Fallback for unmapped fields: convert camelCase/snake_case into a readable label.
  return fieldName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
};

// Sentinel encoding: -1 stored in DB means the switch was OFF when saved.
// On save: if the switch is off, write -1 instead of the actual value.
// On load: if the value is -1, restore switch=false and value=0.
// Sentinel pairs that need -1 encoding when their switch is OFF.
// pgIntangibles/pgOverride/corpIntangibles/corpOverride custom points are intentionally
// absent — their switch and points are now real DB columns, so no sentinel is needed.
const INTANGIBLES_OVERRIDE_PAIRS: [string, string][] = [];

function encodeSentinels(data: Record<string, any>): Record<string, any> {
  const result = { ...data };
  for (const [cpKey, swKey] of INTANGIBLES_OVERRIDE_PAIRS) {
    if (!result[swKey]) result[cpKey] = -1;
  }
  return result;
}

function decodeSentinels(fields: Record<string, any>): void {
  for (const [cpKey, swKey] of INTANGIBLES_OVERRIDE_PAIRS) {
    if (cpKey in fields) {
      if (fields[cpKey] === -1) {
        fields[swKey] = false;
        fields[cpKey] = 0;
      } else if (typeof fields[cpKey] === "number" && fields[cpKey] >= 0) {
        fields[swKey] = true;
      }
    }
  }
}

interface ConflictField {
  field: string;
  label: string;
  userValue: any;
  pipelineValue: any;
  userActor: string;
  userChangedAt: string | null;
}

const LoanCalculate = ({ currentStep, setCurrentStep }: LoanCalculateProps) => {
  const [previousStep, setPreviousStep] = useState<"Loans" | "Application" | "Score" | "Review" | "Underwrite">(currentStep);
  const [isCheckingCriteria, setIsCheckingCriteria] = useState(false);
  const [criteriaChecked, setCriteriaChecked] = useState(false);
  const [justProcessedLoan, setJustProcessedLoan] = useState(false);
  
  // Intercept step changes to check criteria before moving to Score and log workflow actions
  useEffect(() => {
    setActiveStep(currentStep);

    // Reset to default state when going back to Loans
    if (currentStep === "Loans" && previousStep !== "Loans") {
      setLoanStatus(null);
      setStatusFilter("New");
      setSearchTerm("");
      fetchData("", "New", 1);
    }

    // If we just moved from Application to Score, check criteria (only once) — skip for view-only loans
    if (previousStep === "Application" && currentStep === "Score" && !isCheckingCriteria && !criteriaChecked && !isLockedStatus) {
      console.log("Intercepting step change from Application to Score, checking criteria...");
      setIsCheckingCriteria(true);
      const checkAndProceed = async () => {
        const canContinue = checkCriteria();
        console.log("Criteria check result:", canContinue);
        if (!canContinue) {
          // Modal will be shown, revert back to Application step
          console.log("Criteria failed, reverting to Application step and showing modal");
          setCurrentStep("Application");
          setIsCheckingCriteria(false);
          return;
        }
        
        // If criteria passed, allow move to Score
        setCriteriaChecked(true);
        setIsCheckingCriteria(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      };
      checkAndProceed();
    } else if (
      previousStep === "Application" &&
      currentStep === "Score" &&
      !customerExposureChecked &&
      !inputs.customerExposureAcknowledged &&
      !isLockedStatus &&
      Object.keys(modifiedFields).length === 0
    ) {
      // Customer Exposure acknowledgment gate — fires after criteria pass (or were already checked).
      // Guard: only fires when there are no pending field modifications; if there are,
      // criteriaChecked is already false so Step A will catch the next Next click.
      setCurrentStep("Application");
      setShowExposureModal(true);
    } else if (currentStep !== previousStep) {
      // Log workflow action for step transitions (except Review which is handled above)
      const logStepTransition = async () => {
        if (currentStep === "Application" && previousStep === "Loans") {
          // Only log "application" if we didn't just process a loan (process is already logged)
          if (!justProcessedLoan) {
            try {
              await logWorkflowAction(
                String(inputs.applicationNumber || ""),
                "application",
                inputs
              );
            } catch (error) {
              console.error("Error logging application step:", error);
            }
          } else {
            // Reset the flag after skipping the log
            setJustProcessedLoan(false);
          }
        } else if (currentStep === "Score" && previousStep === "Application") {
          try {
            await logWorkflowAction(
              String(inputs.applicationNumber || ""),
              "score",
              inputs
            );
          } catch (error) {
            console.error("Error logging score step:", error);
          }
        } else if (currentStep === "Review" && previousStep === "Score") {
          // Check if intangibles/override were changed AND are ON — require a reason before Review
          if (scoreStepDirtyRef.current && !isLockedStatus) {
            const curr = inputsRef.current;
            const changedKeys = new Set(
              Object.keys(curr).filter(
                (k) => String((curr as any)[k]) !== String((initialValues as any)[k] ?? "")
              )
            );
            const SCORE_REASON_CHECKS = [
              { switchKey: "pgIntangiblesSwitch",  pointsKey: "pgIntangiblesCustomPoints",   label: "PG Intangibles"   },
              { switchKey: "pgOverrideSwitch",     pointsKey: "pgOverrideCustomPoints",      label: "PG Override"      },
              { switchKey: "intangiblesSwitch",    pointsKey: "corpIntangiblesCustomPoints", label: "Corp Intangibles" },
              { switchKey: "overrideSwitch",       pointsKey: "corpOverrideCustomPoints",    label: "Corp Override"    },
              { switchKey: "preferredVendorSwitch", pointsKey: "preferredVendorPoints",     label: "Corp Preferred Vendor" },
            ] as const;
            const needs = SCORE_REASON_CHECKS
              .filter(
                (c) =>
                  (curr as any)[c.switchKey] &&
                  (changedKeys.has(c.switchKey) || changedKeys.has(c.pointsKey))
              )
              .map((c) => ({
                key: c.pointsKey,
                label: c.label,
                prevVal: (initialValues as any)[c.pointsKey] ?? 0,
                newVal: (curr as any)[c.pointsKey],
              }));
            if (needs.length > 0) {
              setCurrentStep("Score");
              setScoreNeedsReason(needs);
              setShowScoreNotesModal(true);
              return;
            }
          }
          try {
            await logWorkflowAction(
              String(inputs.applicationNumber || ""),
              "review",
              inputs
            );
          } catch (error) {
            console.error("Error logging review step:", error);
          }
          if (scoreStepDirtyRef.current) {
            const currentInputs = inputsRef.current;
            const scoreChangedFields: Record<string, any> = {};
            for (const key of Object.keys(currentInputs) as (keyof typeof currentInputs)[]) {
              const prev = (initialValues as any)[key];
              const curr = (currentInputs as any)[key];
              if (prev !== undefined && String(prev) !== String(curr)) {
                scoreChangedFields[key as string] = {
                  previousVal: prev,
                  newVal: curr,
                  label: toDisplayLabel(key as string),
                };
              }
            }
            try {
              await axios.post(
                `${serverConfig.api_base_url}underwriting-engine/save-application`,
                {
                  applicationNumber: currentInputs.applicationNumber,
                  formData: encodeSentinels(currentInputs),
                  actor: UserService.getUsername() || UserService.getEmail() || null,
                  changedFields: Object.keys(scoreChangedFields).length > 0 ? scoreChangedFields : undefined,
                  notes: {},
                },
                { headers: { "Content-Type": "application/json" } }
              );
              setInitialValues(currentInputs);
              scoreStepDirtyRef.current = false;
            } catch (error) {
              console.error("Error saving score data on Review:", error);
            }
          }
        } else if (currentStep === "Underwrite" && previousStep === "Review") {
          try {
            await logWorkflowAction(
              String(inputs.applicationNumber || ""),
              "underwrite",
              inputs
            );
          } catch (error) {
            console.error("Error logging underwrite step:", error);
          }
        }
      };
      logStepTransition();
      
      // Reset criteria checked flag when moving away from Review
      if (currentStep !== "Review") {
        setCriteriaChecked(false);
      }
      setPreviousStep(currentStep);
      setIsCheckingCriteria(false);
    }
  }, [currentStep, previousStep, isCheckingCriteria, criteriaChecked]);
  const [token, setToken] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [company, setCompany] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [userCreditLimit, setUserCreditLimit] = useState<number | null>(null);

  const [loanStatus, setLoanStatus] = useState<string | null>(null);
  const isLockedStatus = isTerminalStatus(loanStatus);
  const isReadOnly = currentStep === "Review" || isLockedStatus;

  const [showAlert, setShowAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");

  // LoanTable related
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("New");
  const [tableData, setTableData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const lockExpiryTimerRef = useRef<number | null>(null);
  const lockPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentStepRef = useRef(currentStep);
  const previousStepRef = useRef(currentStep);
  const activeLockedLoanRef = useRef<string | null>(null);
  const [showTakeoverModal, setShowTakeoverModal] = useState(false);
  const [takeoverLockedBy, setTakeoverLockedBy] = useState("");
  const pendingTakeoverLoanRef = useRef<string | null>(null);
  const [showSessionTakenModal, setShowSessionTakenModal] = useState(false);
  const [sessionTakenMessage, setSessionTakenMessage] = useState("");
  const sessionAlertShownRef = useRef(false);
  const [activeStep, setActiveStep] = useState<LoanCalculateProps["currentStep"]>(currentStep);
  const isApplicationEditable = true;

  // Status shown/compared in the UI = the source-owned athena_status value (Title-
  // Case: Credit Review / Data Entry / Credit Hold / Hold / Approved / Rejected /
  // Withdrawn / Syndicate), EXCEPT the UE-owned referred_to_senior flag wins for
  // display (Refer isn't an athena_status value). Null → treated as Credit Review.
  const normStatus = (
    athenaStatus: any,
    referredToSenior?: any,
  ): string | null => {
    if (referredToSenior === true || referredToSenior === "true") return "Referred";
    const s = (athenaStatus ?? "").toString().trim();
    return s || null;
  };

  const getStatusLabel = (status: string | null | undefined) => {
    if (!status) return "Credit Review";
    return status; // athena_status values + "Referred" are already display-ready
  };

  const getStatusClassName = (status: string | null | undefined): string => {
    switch (status) {
      case "Approved":     return "border-success text-success bg-success/10";
      case "Rejected":     return "border-destructive text-destructive bg-destructive/10";
      case "Withdrawn":    return "border-muted-foreground text-muted-foreground bg-muted/10";
      case "Referred":     return "border-blue-400 text-blue-400 bg-blue-400/10";
      case "Syndicate":    return "border-blue-400 text-blue-400 bg-blue-400/10";
      case "Credit Hold":
      case "Hold":         return "border-foreground/50 text-foreground bg-foreground/5";
      case "Data Entry":
      case "Credit Review":
      default:             return "border-transparent bg-secondary text-secondary-foreground";
    }
  };

  const fetchData = async (search?: string, status?: string, page?: number) => {
    const s = search ?? searchTerm;
    const st = status ?? statusFilter;
    const p = page ?? currentPage;
    if (search !== undefined) setSearchTerm(search);
    if (status !== undefined) setStatusFilter(status);
    try {
      const params = new URLSearchParams();
      if (s) params.set("search", s);
      if (st) params.set("status", st);
      params.set("page", String(p));
      params.set("per_page", "10");

      const email = UserService.getEmail();
      const response = await axios.get(
        `${serverConfig.api_base_url}underwriting-engine/loans?${params.toString()}`,
        { headers: { "X-User-Email": email || "" } }
      );
      const json = response.data;

      if (json.status === "success" && Array.isArray(json.data)) {
        const rows = json.data.map((row: any) => ({
          applicationNumber: row["applicationNumber"] ?? row["application_number"],
          businessName: row["nameOfBusiness"] || row["doing_business_as"] || "",
          submitDate: row["submit_date"] ? String(row["submit_date"]).split("T")[0] : "",
          loanAmount: parseFloat(row["loanAmount"] ?? row["submit_amount"] ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          assetType: row["assetType"] ?? row["asset_type"],
          officeName: row["officeName"] ?? row["office_name"],
          officeNumber: row["office_number"] != null ? row["office_number"] : "",
          status: normStatus(row["athena_status"], row["referred_to_senior"]),
          // v2 source-side score (int4), mirrored verbatim. Available for queue
          // display / filtering; null when the source hasn't scored the app.
          autoscore: row["autoscore"] != null ? Number(row["autoscore"]) : null,
          customerExposure: row["customer_exposure"] != null ? parseFloat(row["customer_exposure"]) : null,
        }));
        setTableData(rows);
        setTotalPages(json.total_pages ?? 1);
        setCurrentPage(json.page ?? p);
      } else {
        setTableData([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setTableData([]);
    }
  };

  useEffect(() => {
    fetchData("", "New", 1);
  }, []);

  useEffect(() => {
    const el = document.getElementById("uw-footer-extra");
    if (el) setFooterPortalEl(el);
  }, [currentStep]);

  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);


  useEffect(() => {
    return () => {
      if (lockExpiryTimerRef.current) {
        window.clearTimeout(lockExpiryTimerRef.current);
      }
    };
  }, []);

  const startLockExpiryTimer = (ttlMinutes: number) => {
    if (lockExpiryTimerRef.current) {
      window.clearTimeout(lockExpiryTimerRef.current);
    }
    lockExpiryTimerRef.current = window.setTimeout(() => {
      const step = currentStepRef.current;
      const shouldShowExpiryAlert =
        step === "Application" ||
        step === "Score" ||
        step === "Review" ||
        step === "Underwrite";
      if (!shouldShowExpiryAlert) {
        return;
      }
      openSessionTakenModal("Your process session expired. Click OK to return to the loan queue.");
    }, ttlMinutes * 60 * 1000);
  };

  const openSessionTakenModal = (msg: string) => {
    setSessionTakenMessage(msg);
    setShowTakeoverModal(false);
    setShowConflictModal(false);
    setShowCriteriaModal(false);
    setShowScoreNotesModal(false);
    setShowExposureModal(false);
    setShowSessionTakenModal(true);
  };

  const handleSessionExpired = () => {
    if (sessionAlertShownRef.current) {
      return;
    }
    sessionAlertShownRef.current = true;
    openSessionTakenModal("Your session has expired. Click OK to return to the loan queue.");
    setCurrentStep("Loans");
  };

  const stopLockPoll = () => {
    if (lockPollRef.current) {
      clearInterval(lockPollRef.current);
      lockPollRef.current = null;
    }
  };

  const startLockPoll = (loanId: string) => {
    stopLockPoll();
    lockPollRef.current = setInterval(async () => {
      if (!activeLockedLoanRef.current) { stopLockPoll(); return; }
      try {
        await refreshActiveLoanLock(activeLockedLoanRef.current);
      } catch (err: any) {
        if (err?.response?.status === 423) {
          stopLockPoll();
          activeLockedLoanRef.current = null;
          const detail = err?.response?.data?.detail;
          openSessionTakenModal(
            (typeof detail === "object" ? detail.message : detail) ||
              "Your session has been taken over by another user."
          );
          setCurrentStep("Loans");
        }
      }
    }, 30_000);
  };

  const acquireLoanLock = async (applicationNumber: string) => {
    let token: string | undefined;
    try {
      token = await UserService.ensureFreshToken();
    } catch {
      UserService.doLogout();
      return false;
    }
    try {
      const response = await axios.post(
        `${serverConfig.api_base_url}underwriting-engine/loans/${applicationNumber}/lock`,
        { token }
      );
      const ttlMinutes = Number(response?.data?.lock?.ttl_minutes || 30);
      startLockExpiryTimer(ttlMinutes);
      activeLockedLoanRef.current = String(applicationNumber);
      startLockPoll(String(applicationNumber));
      setAlertMsg("");
      setShowAlert(false);
      return true;
    } catch (err: any) {
      if (
        err?.response?.status === 401 ||
        err?.response?.status === 301 ||
        err?.response?.data?.status === 301
      ) {
        handleSessionExpired();
        return false;
      }
      if (err?.response?.status === 423) {
        const detail = err?.response?.data?.detail;
        const lockedBy = typeof detail === "object" ? detail.locked_by : null;
        const isOtherUser = lockedBy && lockedBy !== UserService.getEmail();
        const ueProductId = mapUrlToProductId("underwriting-engine") as number;
        if (isOtherUser && UserService.hasRole(rolesAtOrAbove(ueProductId, "reviewer"))) {
          pendingTakeoverLoanRef.current = String(applicationNumber);
          setTakeoverLockedBy(lockedBy);
          setShowTakeoverModal(true);
        } else {
          setAlertMsg(
            typeof detail === "object"
              ? detail.message
              : (detail || "This loan is currently being processed by another user.")
          );
          setShowAlert(true);
        }
        return false;
      }
      setAlertMsg("Unable to lock this loan right now. Please try again.");
      setShowAlert(true);
      return false;
    }
  };

  const takeoverLoanLock = async (applicationNumber: string): Promise<boolean> => {
    let token: string | undefined;
    try {
      token = await UserService.ensureFreshToken();
    } catch {
      UserService.doLogout();
      return false;
    }
    try {
      const response = await axios.post(
        `${serverConfig.api_base_url}underwriting-engine/loans/${applicationNumber}/lock/takeover`,
        { token }
      );
      const ttlMinutes = Number(response?.data?.lock?.ttl_minutes || 30);
      startLockExpiryTimer(ttlMinutes);
      activeLockedLoanRef.current = String(applicationNumber);
      startLockPoll(String(applicationNumber));
      return true;
    } catch {
      setAlertMsg("Takeover failed. Please try again.");
      setShowAlert(true);
      return false;
    }
  };

  const refreshActiveLoanLock = async (loanId: string) => {
    let token: string | undefined;
    try {
      token = await UserService.ensureFreshToken();
    } catch {
      UserService.doLogout();
      return;
    }
    const response = await axios.post(
      `${serverConfig.api_base_url}underwriting-engine/loans/${loanId}/lock`,
      { token }
    );
    const ttlMinutes = Number(response?.data?.lock?.ttl_minutes || 30);
    startLockExpiryTimer(ttlMinutes);
  };

  const releaseActiveLoanLock = async (loanId: string) => {
    stopLockPoll();
    let token: string | undefined;
    try {
      token = await UserService.ensureFreshToken();
    } catch {
      UserService.doLogout();
      return;
    }
    await axios.post(
      `${serverConfig.api_base_url}underwriting-engine/loans/${loanId}/unlock`,
      { token }
    );
  };

  useEffect(() => {
    const prevStep = previousStepRef.current;
    if (prevStep === currentStep) {
      return;
    }
    previousStepRef.current = currentStep;

    const activeLoanId = activeLockedLoanRef.current;
    if (!activeLoanId) {
      return;
    }

    const runStepLockFlow = async () => {
      const isWorkStep =
        currentStep === "Application" ||
        currentStep === "Score" ||
        currentStep === "Review" ||
        currentStep === "Underwrite";

      try {
        if (isWorkStep) {
          await refreshActiveLoanLock(activeLoanId);
          return;
        }
        if (currentStep === "Loans") {
          await releaseActiveLoanLock(activeLoanId);
          activeLockedLoanRef.current = null;
          if (lockExpiryTimerRef.current) {
            window.clearTimeout(lockExpiryTimerRef.current);
            lockExpiryTimerRef.current = null;
          }
        }
      } catch (err: any) {
        if (
          err?.response?.status === 401 ||
          err?.response?.status === 301 ||
          err?.response?.data?.status === 301
        ) {
          handleSessionExpired();
          return;
        }
        if (err?.response?.status === 423) {
          stopLockPoll();
          activeLockedLoanRef.current = null;
          const detail = err?.response?.data?.detail;
          openSessionTakenModal(
            (typeof detail === "object" ? detail.message : detail) ||
              "Your process lock expired or this loan is locked by another user."
          );
          return;
        }
        console.error("Error managing loan lock on step change:", err);
      }
    };

    runStepLockFlow();
  }, [currentStep]);

  const handleLoanProcess = async (applicationNumber: string, rowStatus?: string | null, officeNumber?: string | number | null) => {
    const isReadOnlyLoan = isTerminalStatus(rowStatus);

    // Validate token with Keycloak and fetch user email + credit limit
    let fetchedCreditLimit = 0;
    try {
      const email = UserService.getEmail() || "";
      setUserEmail(email);

      if (email) {
        const officeParam = officeNumber != null ? `?office_number=${encodeURIComponent(officeNumber)}` : "";
        const limitRes = await axios.get(
          `${serverConfig.api_base_url}underwriting-engine/user-limit${officeParam}`,
          { headers: { "X-User-Email": email } }
        );
        const limitJson = limitRes.data;
        fetchedCreditLimit = typeof limitJson.limit === "number" ? limitJson.limit : 0;
        setUserCreditLimit(fetchedCreditLimit);
      }
    } catch {
      setUserCreditLimit(0);
    }

    if (!isReadOnlyLoan) {
      const lockAcquired = await acquireLoanLock(applicationNumber);
      if (!lockAcquired) {
        return;
      }
    }

    setIsLoading(true);
    const startTime = Date.now();
    
    // Fetch data and ensure minimum 1 second delay
    const [res] = await Promise.all([
      axios.get(`${serverConfig.api_base_url}underwriting-engine/loans/${applicationNumber}`),
      new Promise(resolve => setTimeout(resolve, 1000)) // Minimum 1 second delay
    ]);

    const json = res.data;
    const elapsedTime = Date.now() - startTime;
    
    // If the request completed in less than 1 second, wait for the remaining time
    if (elapsedTime < 1000) {
      await new Promise(resolve => setTimeout(resolve, 1000 - elapsedTime));
    }

    if (json.status === "success") {
      const loanData = json.data || {};
      const getLoanField = (...keys: string[]) => {
        for (const key of keys) {
          if (Object.prototype.hasOwnProperty.call(loanData, key)) {
            return loanData[key];
          }
        }
        return undefined;
      };
      // LW returns negative values for fields with no data — map to "" so policy
      // conditions can distinguish "absent" from an explicitly-entered value of 0.
      const lwSentinel = (v: any) => (v != null && !Number.isNaN(Number(v)) && Number(v) < 0) ? "" : v;

      const mappedData = {
        // Each getLoanField call tries demo_contracts keys first, then created_contracts camelCase keys
        applicationNumber: Number(getLoanField("Application Number", "applicationNumber")) || 0,
        nameOfBusiness: getLoanField("Name of Business", "nameOfBusiness"),
        officeName: getLoanField("Office Name", "officeName"),
        loanAmount: Number(getLoanField("Submit Cost", "loanAmount")) || 0,
        approvedAmount: (() => { const v = getLoanField("approvedAmount", "approval_amount"); return v != null ? Number(v) : null; })(),
        // Detail endpoint now overlays customer_exposure (customer's total owned
        // exposure) just like the list; fall back to the list row only if absent.
        customerExposure: getLoanField("customer_exposure") != null
          ? parseFloat(getLoanField("customer_exposure"))
          : (tableData.find(r => String(r.applicationNumber) === String(applicationNumber))?.customerExposure ?? null),
        customerExposureAcknowledged: getLoanField("customerExposureAcknowledged", "customer_exposure_acknowledged") === true,
        assetType: getLoanField("Asset Type", "assetType"),
        // v2 source-side score (mirrored, read-only). Available for display/filtering.
        autoscore: getLoanField("autoscore") != null ? Number(getLoanField("autoscore")) : null,
        // v2 source PG flag ("a PG exists on the deal"). Read-only; serialize also
        // aliases it to requiresPersonalGuarantor. Surfaced here so the source fact
        // reaches the UI. NOTE: the local criteria Rule A still computes its own
        // requiresPersonalGuarantor ("underwriting requires a PG") — kept separate.
        personalGuarantorFlag:
          getLoanField("personal_guarantor_flag", "requiresPersonalGuarantor") != null
            ? Number(getLoanField("personal_guarantor_flag", "requiresPersonalGuarantor"))
            : null,
        city: getLoanField("Customer Billing City", "city"),
        submitDate: getLoanField("Submit Date", "submit_date", "submitDate", "submittedDate"),
        avgHighCredit: getLoanField("AVG_HI_CREDIT", "avg_hi_credit", "avgHighCredit"),
        highCredit: getLoanField("HIGH_CREDIT", "high_credit", "highCredit"),
        numberOfEmployees: getLoanField("EMP_TOT", "emp_tot", "numberOfEmployees"),
        birthyear: getLoanField("BIRTHYEAR", "birthyear"),
        commercialCreditScore: getLoanField("COM_CRE_SCORE", "com_cre_score", "commercialCreditScore"),
        timeInBusiness: getLoanField("LW_YRS_IN_BUS", "lw_yrs_in_bus", "timeInBusiness"),
        standardCode: getLoanField("SIC_PRI", "sic_pri", "standardCode"),
        startYear: getLoanField("START_YEAR", "start_year", "startYear"),
        noOfInquiries: getLoanField("NUM_INQ", "num_inq", "noOfInquiries"),
        noOfTradeExperiences: getLoanField("NUM_TRADE_EXP", "num_trade_exp", "noOfTradeExperiences"),
        pnMasterScore: getLoanField("PN_MASTER_SCR", "pn_master_scr", "pnMasterScore"),
        udf_pass_score: lwSentinel(getLoanField("udf_pass_score")),
        pnHighCredit: getLoanField("paynet_high_credit", "pnHighCredit"),
        pnNoOfTradeExperiences: getLoanField("paynet_number_of_trade_lines", "pnNoOfTradeExperiences"),
        paydex: getLoanField("PAYDEX_FIRM", "paydex_firm", "paydex"),
        vantage: getLoanField("VANTAGE", "vantage"),
        noOfSatisfactoryAccounts: getLoanField("SATACCTS", "sataccts", "noOfSatisfactoryAccounts"),
        noOfTotalDelinquencies: getLoanField("WASDELINQ", "wasdelinq", "noOfTotalDelinquencies"),
        noOfDelinquencies: getLoanField("NUMDELINQ", "numdelinq", "noOfDelinquencies"),
        suits: getLoanField("SUITS", "suits"),
        liens: getLoanField("LIENS", "liens"),
        judgements: getLoanField("JUDGEMENTS", "judgements"),
        udf_fico_average: lwSentinel(getLoanField("udf_fico_average")),
        udf_fico_max: lwSentinel(getLoanField("udf_fico_max")),
        udf_fico: lwSentinel(getLoanField("udf_fico")),
        // Bank-statement criteria, kept exactly as the pipeline sends them
        ...Object.fromEntries(BANK_STATEMENT_FIELDS.map(({ field }) => [field, getLoanField(field)])),
        noOfGuarantors: getLoanField("GRNTRS", "grntrs", "noOfGuarantors"),
        bankruptcy: getLoanField("BANKRUPTCY", "bankruptcy"),
        totalRevolvingBalance: getLoanField("TOTREV_BAL", "totrev_bal", "totalRevolvingBalance"),
        totalRevolvingAvailable: getLoanField("TOTREV_AVAIL", "totrev_avail", "totalRevolvingAvailable"),
        realStateBalance: getLoanField("RE_BAL", "re_bal", "realStateBalance"),
        yield: getLoanField("yield_fe", "YIELD_FE"),
        yield_fe: getLoanField("yield_fe", "YIELD_FE"),
        optimismIndex: Number(getLoanField("Optimism_Index", "optimism_index", "optimismIndex")) || 0,
        gdpGrowthRate: Number(getLoanField("GDP_Growth_Rate", "gdp_growth_rate", "gdpGrowthRate")) || 0,
        cpiIndex: Number(getLoanField("CPI_Index", "cpi_index", "cpiIndex")) || 0,
        fedFundsRate: Number(getLoanField("Fed_Funds_Rate", "fed_funds_rate", "fedFundsRate")) || 0,
        industrialProductionIndex:
          Number(getLoanField("Industrial_Production_Index", "industrial_production_index", "industrialProductionIndex")) || 0,
        unemploymentRate: Number(getLoanField("Unemployment_Rate", "unemployment_rate", "unemploymentRate")) || 0,
        cpiMoM: Number(getLoanField("CPI_MoM", "cpi_mom", "cpiMoM")) || 0,
        cpiRoll3M: Number(getLoanField("CPI_Roll3M", "cpi_roll3m", "cpiRoll3M")) || 0,
        gdpRoll3Q: Number(getLoanField("GDP_Roll3Q", "gdp_roll3q", "gdpRoll3Q")) || 0,
        unemploymentRoll3M: Number(getLoanField("Unemployment_Roll3M", "unemployment_roll3m", "unemploymentRoll3M")) || 0,
        optimismRoll6M: Number(getLoanField("Optimism_Roll6M", "optimism_roll6m", "optimismRoll6M")) || 0,
        industrialProdChange6M:
          Number(getLoanField("IndustrialProd_Change6M", "industrialprod_change6m", "industrialProdChange6M")) || 0,
        covidFlag: Number(getLoanField("COVID_Flag", "covid_flag", "covidFlag")) || 0,
        optimismIndexNormalized:
          Number(getLoanField("Optimism_Index_Normalized", "optimism_index_normalized", "optimismIndexNormalized")) || 0,
        unemploymentRateNormalized:
          Number(getLoanField("Unemployment_Rate_Normalized", "unemployment_rate_normalized", "unemployment_rate_Normalized", "unemploymentRateNormalized")) || 0,
        gdpGrowthRateNormalized:
          Number(getLoanField("GDP_Growth_Rate_Normalized", "gdp_growth_rate_normalized", "gdpGrowthRateNormalized")) || 0,
        actualRating: getLoanField("Risk Rating", "actualRating"),
        actualScore: "NA",
        isNewApplication: false,
      };

      const sanitizedMappedData = sanitizeNonNegativeFields(mappedData);

      let useCorpSwitch = false;
      let usePGSwitch = false;

      const _ficoVal = (v: any) => v === "" || v == null;
      const ficoEmpty = _ficoVal(sanitizedMappedData.udf_fico) && _ficoVal(sanitizedMappedData.udf_fico_average) && _ficoVal(sanitizedMappedData.udf_fico_max);
      if (!ficoEmpty && sanitizedMappedData.noOfGuarantors > 0) {
        usePGSwitch = true;
      } else {
        useCorpSwitch = true;
      }

      setLoanStatus(normStatus(loanData["athena_status"] ?? json.loan_status, loanData["referred_to_senior"]));

      // Reset all state when processing a new application FIRST
      setModifiedFields({});
      setFieldNotes({});
      setCriteriaChecked(false);
      setCustomerExposureChecked(false);
      setShowCriteriaModal(false);
      setAcceptOverride(false);
      scoreStepDirtyRef.current = false;
      setFailedRules([]);
      setCriteriaMessage("");
      
      // Calculate all auto-updated values upfront
      const timeInBusiness = sanitizedMappedData.timeInBusiness || 0;
      const fico = sanitizedMappedData.udf_fico_average || 0;
      const numberOfEmployees = sanitizedMappedData.numberOfEmployees || 0;
      const avgHighCredit = sanitizedMappedData.avgHighCredit || 0;
      const paydexRaw = sanitizedMappedData.paydex;
      const paydexAbsent = paydexRaw == null || paydexRaw === "";
      const paydex = paydexAbsent ? 0 : Number(paydexRaw);
      const noOfTradeExperiences = sanitizedMappedData.noOfTradeExperiences || 0;

      // Calculate auto-updated PG fields
      const pgTimeInBusiness = timeInBusiness;
      const pgFicoScore = fico;

      let pgFicoPoints = 0;
      if (pgFicoScore > 725)       pgFicoPoints = 2;
      else if (pgFicoScore >= 650) pgFicoPoints = 1;

      let pgTimeInBusinessPoints = 0;
      if (pgTimeInBusiness > 5)       pgTimeInBusinessPoints = 2;
      else if (pgTimeInBusiness >= 3) pgTimeInBusinessPoints = 1;

      // Calculate auto-updated Corp fields and points (grid chosen by loan amount)
      const {
        corpTimeInBusiness,
        corpTimeInBusinessPoints,
        corpNumberOfEmployees,
        corpNumberOfEmployeesPoints,
        corpHighCredit,
        corpHighCreditPoints,
        corpTradeLines,
        corpTradeLinesPoints,
      } = corpGridFields({
        ...sanitizedMappedData,
        timeInBusiness,
        numberOfEmployees,
        highCredit: sanitizedMappedData.highCredit || 0,
        noOfTradeExperiences,
      });

      const corpPaydex = paydex;
      let corpPaydexPoints = 0;
      if (!paydexAbsent) {
        if (corpPaydex >= 70) corpPaydexPoints = 2;
        else if (corpPaydex >= 60) corpPaydexPoints = 1;
        else corpPaydexPoints = -1;
      }

      // Pull any previously-saved score fields back from created_contracts.
      // The DB returns them with the same camelCase keys as initialInputs,
      // so a single pass picks up all PG/Corp toggles, points, and ratings.
      // LW-sourced fields that carry negative sentinel values are clamped here
      // so they never pollute savedScoreFields with stale negatives.
      const LW_EMPTY_FIELDS = new Set(['udf_fico_average', 'udf_fico_max', 'udf_fico', 'pgFicoScore']);
      const LW_ZERO_FIELDS = new Set(['realStateBalance']);
      const savedScoreFields: Record<string, any> = {};
      for (const key of Object.keys(initialInputs)) {
        const val = loanData[key];
        if (val !== undefined && val !== null) {
          const isNeg = !Number.isNaN(Number(val)) && Number(val) < 0;
          if (isNeg && LW_EMPTY_FIELDS.has(key)) {
            savedScoreFields[key] = "";
          } else if (isNeg && LW_ZERO_FIELDS.has(key)) {
            savedScoreFields[key] = "0";
          } else {
            savedScoreFields[key] = val;
          }
        }
      }
      decodeSentinels(savedScoreFields);

      // Custom points share the same DB column as base points (pgintangiblespoints etc.).
      // Populate the custom field from the DB-loaded base value so the user sees their
      // previously-saved value; only do this when the custom key wasn't already set.
      const CUSTOM_FROM_BASE: [string, string][] = [
        ["pgIntangiblesCustomPoints",   "pgIntangiblesPoints"],
        ["pgOverrideCustomPoints",      "pgOverridePoints"],
        ["corpIntangiblesCustomPoints", "intangiblesPoints"],
        ["corpOverrideCustomPoints",    "overridePoints"],
      ];
      for (const [cpKey, baseKey] of CUSTOM_FROM_BASE) {
        if (!(cpKey in savedScoreFields) && savedScoreFields[baseKey] !== undefined) {
          savedScoreFields[cpKey] = savedScoreFields[baseKey];
        }
      }

      // Compute final FICO early so PG/Corp switch can be pinned after savedScoreFields
      const finalAvgFico = (() => { const v = savedScoreFields.udf_fico_average ?? sanitizedMappedData.udf_fico_average; return Number(v) < 0 ? "" : (v ?? ""); })();
      const finalMinFico = (() => { const v = savedScoreFields.udf_fico ?? sanitizedMappedData.udf_fico; return Number(v) < 0 ? "" : (v ?? ""); })();
      const finalMaxFico = (() => { const v = savedScoreFields.udf_fico_max ?? sanitizedMappedData.udf_fico_max; return Number(v) < 0 ? "" : (v ?? ""); })();
      const _fEmpty = (v: any) => v === "" || v == null;
      const ficoIsEmpty = _fEmpty(finalMinFico) && _fEmpty(finalAvgFico) && _fEmpty(finalMaxFico);

      // Build complete initial data with all auto-updated fields
      const completeInitialData = {
        ...initialInputs,
        ...sanitizedMappedData,
        // Auto-updated PG fields (fallback if not yet saved)
        pgTimeInBusiness,
        pgFicoPoints,
        pgTimeInBusinessPoints,
        // Auto-updated Corp fields (fallback if not yet saved)
        corpTimeInBusiness,
        corpTimeInBusinessPoints,
        corpNumberOfEmployees,
        corpNumberOfEmployeesPoints,
        corpTradeLines,
        corpTradeLinesPoints,
        // Saved score step data from created_contracts wins over all auto-calculated defaults
        ...savedScoreFields,
        // These always mirror live D&B data — never use stale saved values
        corpHighCredit,
        corpHighCreditPoints,
        corpPaydex: String(corpPaydex),
        corpPaydexPoints,
        // realStateBalance: LW returns negative when no data — always clamp to "0" for display
        realStateBalance: (() => {
          const v = savedScoreFields.realStateBalance ?? sanitizedMappedData.realStateBalance;
          return Number(v) < 0 ? "0" : (v ?? "");
        })(),
        // FICO fields: LW returns negative when no data — always show empty
        udf_fico_average: finalAvgFico,
        udf_fico_max: (() => { const v = savedScoreFields.udf_fico_max ?? sanitizedMappedData.udf_fico_max; return Number(v) < 0 ? "" : (v ?? ""); })(),
        udf_fico: (() => { const v = savedScoreFields.udf_fico ?? sanitizedMappedData.udf_fico; return Number(v) < 0 ? "" : (v ?? ""); })(),
        pgFicoScore: (() => {
          if (savedScoreFields.pgFicoScore !== undefined && savedScoreFields.pgFicoScore !== null && savedScoreFields.pgFicoScore !== "") {
            const sv = Number(savedScoreFields.pgFicoScore);
            return sv < 0 ? "" : String(savedScoreFields.pgFicoScore);
          }
          const avg = sanitizedMappedData.udf_fico_average;
          const plain = sanitizedMappedData.udf_fico;
          const high = sanitizedMappedData.udf_fico_max;
          const v = (Number(avg) > 0 ? avg : null) ?? (Number(plain) > 0 ? plain : null) ?? (Number(high) > 0 ? high : null) ?? "";
          return Number(v) < 0 ? "" : (v ?? "");
        })(),
        // approvedAmount: fall back to loan amount only if null (0 is kept as-is)
        approvedAmount: (() => {
          const v = sanitizedMappedData.approvedAmount;
          return v != null ? v : (sanitizedMappedData.loanAmount || 0);
        })(),
        // PG/Corp switch: null/empty FICO means PG scoring is impossible — force corp.
        // These must be pinned AFTER ...savedScoreFields so stale DB values don't override.
        usePGSwitch: ficoIsEmpty ? false : usePGSwitch,
        useCorpSwitch: ficoIsEmpty ? true : useCorpSwitch,
      };

      // Set initial values and inputs with complete data (including auto-updated fields)
      setInitialValues(completeInitialData);
      setOriginalBackendData(json.data); // Store original backend response
      setInputs(completeInitialData);

      try {
        await logWorkflowAction(applicationNumber, "process", completeInitialData);
      } catch (error) {
        console.error("Error logging process loan action:", error);
      }

      // Set flag to skip "application" logging since "process" was just logged
      setJustProcessedLoan(true);

      const currentLoanStatus = normStatus(loanData["athena_status"] ?? json.loan_status, loanData["referred_to_senior"]);
      const loanAmt = sanitizedMappedData.loanAmount || 0;
      const isReferred = currentLoanStatus === "Referred";
      const isLocked = isTerminalStatus(currentLoanStatus);
      const hasEnoughLimit = fetchedCreditLimit >= loanAmt;

      const targetStep = isLocked || isReferred ? "Underwrite" : "Application";
      if (!isLocked) try {
        const conflictRes = await axios.get(
          `${serverConfig.api_base_url}underwriting-engine/pipeline-conflict/${applicationNumber}`,
          { headers: { "X-User-Email": UserService.getEmail() || "" } }
        );
        const conflictJson = conflictRes.data;
        const conflicts: ConflictField[] = conflictJson?.data || [];
        if (conflicts.length > 0) {
          setConflictFields(conflicts);
          setConflictSelections({});
          setPendingAppNumber(Number(applicationNumber));
          setPendingOfficeNumber(officeNumber ?? null);
          setShowConflictModal(true);
          setIsLoading(false);
          return;
        }
      } catch {
        // conflict check failed — proceed normally
      }
      setCurrentStep(targetStep);
    } else {
      setAlertMsg("Failed to fetch loan details");
      setShowAlert(true);
    }
    setIsLoading(false);
  };

  // Asset types and office names - keeping from original
  const assetTypes = [
    "DRNK", "ADIA", "ICEM", "PRNT", "LOAD", "FORK", "CNST", "EXCV", "HVAC",
    "TTLV", "CCTV", "CNCM", "ALFT", "OFEQ", "SWAR", "GYM", "REST", "AIRD",
    "PRNM", "FONE", "REFR", "WELD", "GRIN", "XRAY", "MLAS", "FURN", "DISH",
    "LAWN", "EMBR", "CMPC", "ACQU", "MEFB", "MEDI", "INEQ", "DMED", "BEAU",
    "SLIT", "LEAS", "COMP", "CANO", "MILL", "MANU", "LAB", "FOOD", "AV",
    "INFL", "FLSW", "GASP", "POS", "SAWS", "MFRM", "WHAL", "COPY", "WATT",
    "SERV", "PACK", "AIRC", "WOOD", "ENGR", "RETC", "WATC", "FRAL", "LOGG",
    "FRCH", "SIGN", "SLPR", "IRRI", "AIRP", "PNT", "METL", "TENT", "PAVE",
    "ALRM", "GEN", "LAMN", "DUMP", "PRES", "SPRT", "CLNR", "IPRT", "DENT",
    "DOZE", "GPS", "TAN", "LITU", "FARM", "PALT", "WATP", "WDRY", "FOTO",
    "MOBR", "MALT", "SAFE", "HYMS", "MLAT", "PSHR", "SCAL", "ROLL", "CRPT",
    "SURV", "CNVY", "TRSH", "ACCE", "SCRN", "CHAI", "SCAN", "EPPA", "REFU",
    "DYNO", "ULTS", "VEND", "MUSC", "LBLI", "ALCH", "PORT", "OFPT", "TV",
    "PLMB", "SURG", "TRDB", "CADD", "BOWL", "CREM", "PBLD", "BRKL", "CLAU",
    "MELR", "MAIL", "INSU", "TOWH", "UNKN", "PAGE", "ARCH", "HYDR",
  ];

  const officeName = [
    "Partner Funding - Credit",
    "Strategic Accounts",
    "End User Channel",
    "Dealer Services",
    "Partner Funding - Asset",
    "Copier",
    "Healthcare",
    "Specialty Vehicles",
    "NSC NE",
    "UCB EF",
    "Franchise Finance",
    "Navitas Capital",
    "Enterprise Dealer",
    "PFC Franchise",
  ];

  // Initial inputs state - keeping all fields from original
  const initialInputs = {
    isNewApplication: true,
    applicationNumber: 0,
    nameOfBusiness: "",
    officeName: "",
    loanAmount: 0,
    approvedAmount: 0,
    customerExposure: null as number | null,
    customerExposureAcknowledged: false as boolean,
    assetType: "",
    city: "",
    avgHighCredit: "",
    highCredit: "",
    bankruptcy: "",
    birthyear: "",
    commercialCreditScore: "",
    numberOfEmployees: "",
    udf_fico_average: "",
    udf_fico_max: "",
    udf_fico: "",
    noOfGuarantors: "",
    noOfCorpGuarantors: "",
    suits: "",
    liens: "",
    judgements: "",
    timeInBusiness: "",
    noOfDelinquencies: "",
    noOfInquiries: "",
    noOfTradeExperiences: "",
    paydex: "",
    pnMasterScore: "",
    udf_pass_score: "",
    pnHighCredit: "",
    pnNoOfTradeExperiences: "",
    realStateBalance: "",
    realEstateStatus: "",
    noOfSatisfactoryAccounts: "",
    standardCode: "",
    startYear: "",
    totalRevolvingBalance: "",
    totalRevolvingAvailable: "",
    vantage: "",
    noOfTotalDelinquencies: "",
    yield: "",
    udf_min_100k_avg_bal_3mo: "",
    udf_min_annual_dep_5_million: "",
    udf_min_250k_avg_bal_3mo: "",
    udf_min_annual_dep_10_million: "",
    optimismIndex: 0,
    gdpGrowthRate: 0,
    cpiIndex: 0,
    fedFundsRate: 0,
    industrialProductionIndex: 0,
    unemploymentRate: 0,
    cpiMoM: 0,
    cpiRoll3M: 0,
    gdpRoll3Q: 0,
    unemploymentRoll3M: 0,
    optimismRoll6M: 0,
    industrialProdChange6M: 0,
    covidFlag: 0,
    optimismIndexNormalized: 0,
    unemploymentRateNormalized: 0,
    gdpGrowthRateNormalized: 0,
    pgTimeInBusiness: "",
    pgFicoScore: "",
    pgStrongPaymentHistorySwitch: false,
    pgStrongPaymentHistorySwitchPoints: 2,
    pgAcceptableCreditHistorySwitch: false,
    pgAcceptableCreditHistorySwitchPoints: 1,
    pgStrongComparibleCreditSwitch: false,
    pgStrongComparibleCreditSwitchPoints: 2,
    pgAcceptableComparibleCreditSwitch: false,
    pgAcceptableComparibleCreditSwitchPoints: 1,
    pgPreferredCollateralSwitch: false,
    pgPreferredCollateralSwitchPoints: 1,
    pgSoftCollateralSwitch: false,
    pgSoftCollateralSwitchPoints: -1,
    pgIntangiblesSwitch: false,
    pgIntangiblesPoints: 0,
    pgIntangiblesCustomPoints: 0,
    pgOverrideSwitch: false,
    pgOverridePoints: 0,
    pgOverrideCustomPoints: 0,
    pgVendorCheck: false,
    pgFicoPoints: 0,
    pgTimeInBusinessPoints: 0,
    pgVendorSupportSwitch: false,
    pgVendorClearSwitch: false,
    pgAdvancePaymentSwitch: false,
    corpTimeInBusiness: "",
    corpTimeInBusinessPoints: 0,
    corpNumberOfEmployees: "",
    corpNumberOfEmployeesPoints: 0,
    corpHighCredit: "",
    corpHighCreditPoints: 0,
    corpPaydex: "",
    corpPaydexPoints: 0,
    corpTradeLines: "",
    corpTradeLinesPoints: 0,
    suitsLiensJudgements: "",
    suitsLiensJudgementsPoints: 1,
    historyClearSwitch: false,
    historyClearPoints: 1,
    uccFilingsSwitch: false,
    uccFilingsPoints: 1,
    highRiskIndustrySwitch: false,
    highRiskIndustryPoints: -1,
    comparableLeaseSwitch: false,
    comparableLeasePoints: 1,
    preferredVendorSwitch: false,
    preferredVendorPoints: 0,
    majorityEquitySingleOwnershipSwitch: false,
    majorityEquitySingleOwnershipPoints: -2,
    preferredCollateralSwitch: false,
    preferredCollateralPoints: 1,
    softCollateralSwitch: false,
    softCollateralPoints: -1,
    intangiblesSwitch: false,
    intangiblesPoints: 0,
    corpIntangiblesCustomPoints: 0,
    overrideSwitch: false,
    overridePoints: 0,
    corpOverrideCustomPoints: 0,
    anyMajorDerog: false,
    vendorCheck: false,
    corpVendorClearSwitch: false,
    corpVendorSupportSwitch: false,
    corpAdvancePaymentSwitch: false,
    pgScore: 0,
    pgRating: "C",
    corpScore: 0,
    corpRating: "C",
    actualRating: "",
    actualScore: "",
    usePGSwitch: false,
    useCorpSwitch: false,
    requiresPersonalGuarantor: 0,
    corpThreeOfFourCriteriaFailed: 0,
    corpOneOfTwoCriteriaFailed: 0,
    loanShortfallSupportFailed: 0,
    bankStatementCriteriaFailed: 0,
  };

  const [inputs, setInputs] = useState(initialInputs);
  const inputsRef = useRef(initialInputs);
  inputsRef.current = inputs; // always reflects latest inputs without a stale closure
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});
  const [originalBackendData, setOriginalBackendData] = useState<Record<string, any>>({});
  const [failedRules, setFailedRules] = useState<any[]>([]);
  const [criteriaMessage, setCriteriaMessage] = useState<any>("");
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  const [acceptOverride, setAcceptOverride] = useState(false);

  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictFields, setConflictFields] = useState<ConflictField[]>([]);
  const [conflictSelections, setConflictSelections] = useState<Record<string, "user" | "pipeline">>({});
  const [pendingAppNumber, setPendingAppNumber] = useState<number>(0);
  const [pendingOfficeNumber, setPendingOfficeNumber] = useState<string | number | null>(null);
  const [isSubmittingConflict, setIsSubmittingConflict] = useState(false);
  // Application-step edit tracking → drives the mandatory reason prompt.
  const [modifiedFields, setModifiedFields] = useState<Record<string, any>>({});
  const [fieldNotes, setFieldNotes] = useState<Record<string, string>>({});
  const scoreStepDirtyRef = useRef(false);
  // Score-step intangibles/override reason modal
  const [showScoreNotesModal, setShowScoreNotesModal] = useState(false);
  const [scoreNotes, setScoreNotes] = useState<Record<string, string>>({});
  const [scoreNeedsReason, setScoreNeedsReason] = useState<
    { key: string; label: string; prevVal: any; newVal: any }[]
  >([]);
  const [isSubmittingCreditHold, setIsSubmittingCreditHold] = useState(false);
  const [footerPortalEl, setFooterPortalEl] = useState<Element | null>(null);
  // Customer Exposure acknowledgment modal
  const [showExposureModal, setShowExposureModal] = useState(false);
  const [exposureChecked, setExposureChecked] = useState(false);
  const [customerExposureChecked, setCustomerExposureChecked] = useState(false);

  // Calculation functions
  const calculateTotalPGPoints = () => {
    let total = 0;
    if (inputs.pgStrongPaymentHistorySwitch) {
      total += Number(inputs.pgStrongPaymentHistorySwitchPoints) || 0;
    }
    if (inputs.pgAcceptableCreditHistorySwitch) {
      total += Number(inputs.pgAcceptableCreditHistorySwitchPoints) || 0;
    }
    if (inputs.pgStrongComparibleCreditSwitch) {
      total += Number(inputs.pgStrongComparibleCreditSwitchPoints) || 0;
    }
    if (inputs.pgAcceptableComparibleCreditSwitch) {
      total += Number(inputs.pgAcceptableComparibleCreditSwitchPoints) || 0;
    }
    if (inputs.pgPreferredCollateralSwitch) {
      total += Number(inputs.pgPreferredCollateralSwitchPoints) || 0;
    }
    if (inputs.pgSoftCollateralSwitch) {
      total += Number(inputs.pgSoftCollateralSwitchPoints) || 0;
    }
    if (inputs.pgIntangiblesSwitch) {
      total += Number(inputs.pgIntangiblesCustomPoints) || 0;
    }
    if (inputs.pgOverrideSwitch) {
      total += Number(inputs.pgOverrideCustomPoints) || 0;
    }

    setInputs((prev) => {
      const updated = { ...prev };
      updated.pgScore = Math.max(0, total);
      if (total >= 4) {
        updated.pgRating = "A/A1/A Preferred";
      } else if (total >= 3) {
        updated.pgRating = "B1";
      } else if (total >= 2) {
        updated.pgRating = "B2/B3";
      } else {
        updated.pgRating = "C";
      }
      return updated;
    });
  };

  const calculateTotalCorpPoints = () => {
    const microTicket = isMicroTicket(inputs.loanAmount);
    let total = 0;
    if (inputs.corpTimeInBusiness) {
      total += Number(inputs.corpTimeInBusinessPoints) || 0;
    }
    if (inputs.corpNumberOfEmployees) {
      total += Number(inputs.corpNumberOfEmployeesPoints) || 0;
    }
    if (inputs.corpHighCredit) {
      total += Number(inputs.corpHighCreditPoints) || 0;
    }
    total += Number(inputs.corpPaydexPoints) || 0;
    if (inputs.corpTradeLines) {
      total += Number(inputs.corpTradeLinesPoints) || 0;
    }
    if (inputs.suitsLiensJudgements === "any") {
      total += 0;
    } else if (inputs.suitsLiensJudgements === "material") {
      total -= 3;
    } else {
      total += 1;
    }
    if (inputs.historyClearSwitch) {
      total += Number(inputs.historyClearPoints) || 0;
    }
    if (inputs.uccFilingsSwitch) {
      total += Number(inputs.uccFilingsPoints) || 0;
    }
    if (inputs.highRiskIndustrySwitch) {
      total += Number(inputs.highRiskIndustryPoints) || 0;
    }
    // The micro-ticket grid has Preferred Vendor (up to 2) in place of Comparable Lease.
    if (inputs.comparableLeaseSwitch && !microTicket) {
      total += Number(inputs.comparableLeasePoints) || 0;
    }
    if (inputs.preferredVendorSwitch && microTicket) {
      total += Number(inputs.preferredVendorPoints) || 0;
    }
    if (inputs.majorityEquitySingleOwnershipSwitch) {
      total += Number(inputs.majorityEquitySingleOwnershipPoints) || 0;
    }
    if (inputs.preferredCollateralSwitch) {
      total += Number(inputs.preferredCollateralPoints) || 0;
    }
    if (inputs.softCollateralSwitch) {
      total += Number(inputs.softCollateralPoints) || 0;
    }
    if (inputs.intangiblesSwitch) {
      total += Number(inputs.corpIntangiblesCustomPoints) || 0;
    }
    if (inputs.overrideSwitch) {
      total += Number(inputs.corpOverrideCustomPoints) || 0;
    }

    setInputs((prev) => {
      const updated = { ...prev };
      updated.corpScore = Math.max(0, total);
      // Rating is score-driven for all loan sizes. The qualifying / credit
      // criteria are an overridable gate in checkCriteria(), not a rating input.
      if (total >= 10) {
        updated.corpRating = "A1";
      } else if (total >= 9) {
        updated.corpRating = "B1";
      } else if (total >= 8) {
        updated.corpRating = "B2";
      } else {
        updated.corpRating = "C";
      }
      return updated;
    });
  };

  // Calculate PG points only when relevant PG fields change
  useEffect(() => {
    if (inputs.usePGSwitch) {
      calculateTotalPGPoints();
    }
  }, [
    inputs.pgStrongPaymentHistorySwitch,
    inputs.pgStrongPaymentHistorySwitchPoints,
    inputs.pgAcceptableCreditHistorySwitch,
    inputs.pgAcceptableCreditHistorySwitchPoints,
    inputs.pgStrongComparibleCreditSwitch,
    inputs.pgStrongComparibleCreditSwitchPoints,
    inputs.pgAcceptableComparibleCreditSwitch,
    inputs.pgAcceptableComparibleCreditSwitchPoints,
    inputs.pgStrongPaymentHistorySwitch,
    inputs.pgStrongPaymentHistorySwitchPoints,
    inputs.pgAcceptableCreditHistorySwitch,
    inputs.pgAcceptableCreditHistorySwitchPoints,
    inputs.pgStrongComparibleCreditSwitch,
    inputs.pgStrongComparibleCreditSwitchPoints,
    inputs.pgAcceptableComparibleCreditSwitch,
    inputs.pgAcceptableComparibleCreditSwitchPoints,
    inputs.pgPreferredCollateralSwitch,
    inputs.pgPreferredCollateralSwitchPoints,
    inputs.pgSoftCollateralSwitch,
    inputs.pgSoftCollateralSwitchPoints,
    inputs.pgIntangiblesSwitch,
    inputs.pgIntangiblesCustomPoints,
    inputs.pgOverrideSwitch,
    inputs.pgOverrideCustomPoints,
    inputs.pgVendorSupportSwitch,
    inputs.usePGSwitch,
  ]);

  // Calculate Corp points only when relevant Corp fields change
  useEffect(() => {
    if (inputs.useCorpSwitch) {
      calculateTotalCorpPoints();
    }
  }, [
    inputs.corpTimeInBusiness,
    inputs.corpTimeInBusinessPoints,
    inputs.corpNumberOfEmployees,
    inputs.corpNumberOfEmployeesPoints,
    inputs.corpHighCredit,
    inputs.corpHighCreditPoints,
    inputs.corpPaydex,
    inputs.corpPaydexPoints,
    inputs.corpTradeLines,
    inputs.corpTradeLinesPoints,
    inputs.suitsLiensJudgements,
    inputs.suitsLiensJudgementsPoints,
    inputs.historyClearSwitch,
    inputs.historyClearPoints,
    inputs.uccFilingsSwitch,
    inputs.uccFilingsPoints,
    inputs.highRiskIndustrySwitch,
    inputs.highRiskIndustryPoints,
    inputs.comparableLeaseSwitch,
    inputs.comparableLeasePoints,
    inputs.preferredVendorSwitch,
    inputs.preferredVendorPoints,
    inputs.majorityEquitySingleOwnershipSwitch,
    inputs.majorityEquitySingleOwnershipPoints,
    inputs.preferredCollateralSwitch,
    inputs.preferredCollateralPoints,
    inputs.softCollateralSwitch,
    inputs.softCollateralPoints,
    inputs.intangiblesSwitch,
    inputs.corpIntangiblesCustomPoints,
    inputs.overrideSwitch,
    inputs.corpOverrideCustomPoints,
    inputs.loanAmount,
    inputs.useCorpSwitch,
    inputs.corpVendorSupportSwitch,
  ]);

  const handleChange = (event: any) => {
    const { name, type, value } = event.target || {};
    const checked = (event.target as HTMLInputElement)?.checked;
    const rawValue = type === "checkbox" || type === "switch" ? checked : value;
    const newValue = toNonNegativeValue(name, rawValue);

    // Mark Score step dirty so Review only saves when something actually changed.
    if (currentStep === "Score") {
      scoreStepDirtyRef.current = true;
    }

    // Track Application-step edits to source fields for the mandatory reason prompt
    // (the Application → Score criteria modal). Edits persist to uw_working source
    // columns; the reason is logged to uw_field_history.
    if (
      currentStep === "Application" &&
      Object.keys(initialValues).length > 0 &&
      name in initialValues
    ) {
      if (initialValues[name] != newValue) {
        setModifiedFields((prev) => ({
          ...prev,
          [name]: {
            previousVal: initialValues[name],
            newVal: newValue,
            label: toDisplayLabel(name),
          },
        }));
        // A new modification means criteria must be re-checked before proceeding.
        setCriteriaChecked(false);
      } else {
        setModifiedFields((prev) => {
          const newFields = { ...prev };
          delete newFields[name];
          return newFields;
        });
      }
    }

    setInputs((prevInputs) => {
      let updated = { ...prevInputs, [name]: newValue };

      // Preferred Vendor is worth up to 2 points.
      if (name === "preferredVendorPoints" && Number(newValue) > 2) {
        updated.preferredVendorPoints = 2;
      }

      // Auto-update pg fields
      if (name === "timeInBusiness") {
        updated.pgTimeInBusiness = value;
        const t = Number(value);
        updated.pgTimeInBusinessPoints = t > 5 ? 2 : t >= 3 ? 1 : 0;
      }
      if (name === "udf_fico_average" || name === "udf_fico" || name === "udf_fico_max") {
        const avg   = name === "udf_fico_average" ? value : updated.udf_fico_average;
        const plain = name === "udf_fico"         ? value : updated.udf_fico;
        const high  = name === "udf_fico_max"     ? value : updated.udf_fico_max;
        const best  = (Number(avg) > 0 ? avg : null) ?? (Number(plain) > 0 ? plain : null) ?? (Number(high) > 0 ? high : null) ?? "";
        updated.pgFicoScore = best;
        const f = Number(best);
        updated.pgFicoPoints = f > 725 ? 2 : f >= 650 ? 1 : 0;
      }

      // Auto-update corp fields (grid chosen by loan amount)
      const grid = corpGridFields(updated);
      if (name === "timeInBusiness") {
        updated.corpTimeInBusiness = grid.corpTimeInBusiness;
        updated.corpTimeInBusinessPoints = grid.corpTimeInBusinessPoints;
      }
      if (name === "numberOfEmployees") {
        updated.corpNumberOfEmployees = grid.corpNumberOfEmployees;
        updated.corpNumberOfEmployeesPoints = grid.corpNumberOfEmployeesPoints;
      }
      if (name === "highCredit" || name === "pnHighCredit") {
        updated.corpHighCredit = grid.corpHighCredit;
        updated.corpHighCreditPoints = grid.corpHighCreditPoints;
      }
      if (name === "paydex") {
        updated.corpPaydex = value;
        const paydexInputAbsent = value === "" || value == null;
        const corpPaydex = Number(value);
        if (paydexInputAbsent) {
          updated.corpPaydexPoints = 0;
        } else if (corpPaydex >= 70) {
          updated.corpPaydexPoints = 2;
        } else if (corpPaydex >= 60) {
          updated.corpPaydexPoints = 1;
        } else {
          updated.corpPaydexPoints = -1;
        }
      }
      if (name === "noOfTradeExperiences" || name === "pnNoOfTradeExperiences") {
        updated.corpTradeLines = grid.corpTradeLines;
        updated.corpTradeLinesPoints = grid.corpTradeLinesPoints;
      }
      if (name === "suitsLiensJudgements") {
        if (value === "any") {
          updated.suitsLiensJudgementsPoints = 0;
        } else if (value === "material") {
          updated.suitsLiensJudgementsPoints = -3;
        } else {
          updated.suitsLiensJudgementsPoints = 1;
        }
      }

      // pg or corp switch
      if (name === "useCorpSwitch" && checked) {
        updated.usePGSwitch = false;
      }
      if (name === "usePGSwitch" && checked) {
        updated.useCorpSwitch = false;
      }
      if (name === "useCorpSwitch" && !checked && Number(prevInputs.noOfGuarantors) < 1) {
        updated.useCorpSwitch = true;
      }
      if (name === "useCorpSwitch" && !checked && Number(prevInputs.noOfGuarantors) >= 1) {
        updated.usePGSwitch = true;
      }
      if (name === "usePGSwitch" && !checked) {
        updated.useCorpSwitch = true;
      }

      return sanitizeNonNegativeFields(updated);
    });
  };

  useEffect(() => {
    const _fe = (v: any) => v === "" || v == null;
    const ficoEmpty = _fe(inputs.udf_fico) && _fe(inputs.udf_fico_average) && _fe(inputs.udf_fico_max);
    if (!ficoEmpty && Number(inputs.noOfGuarantors) > 0) {
      if (Number(inputs.noOfCorpGuarantors) > 0) {
        setInputs((prev) => ({
          ...prev,
          useCorpSwitch: true,
          usePGSwitch: false,
        }));
      } else {
        setInputs((prev) => ({
          ...prev,
          useCorpSwitch: false,
          usePGSwitch: true,
        }));
      }
    } else {
      setInputs((prev) => ({
        ...prev,
        useCorpSwitch: true,
        usePGSwitch: false,
      }));
    }
  }, [inputs.noOfCorpGuarantors, inputs.noOfGuarantors, inputs.udf_fico, inputs.udf_fico_average, inputs.udf_fico_max]);

  const failCriteria = (title: string, checks: any[] = []) => ({
    title,
    checks,
  });

  const handleScoreNotesAccept = async () => {
    const currentInputs = inputsRef.current;
    const actor = UserService.getUsername() || UserService.getEmail() || null;
    if (!isLockedStatus) {
      const changedFields: Record<string, any> = {};
      for (const k of Object.keys(currentInputs)) {
        const prev = (initialValues as any)[k];
        const curr = (currentInputs as any)[k];
        if (prev !== undefined && String(prev) !== String(curr)) {
          changedFields[k] = { previousVal: prev, newVal: curr, label: toDisplayLabel(k) };
        }
      }
      // Mirror each note onto its companion switch key so the note gets stored
      // on the switch history row (always logged) when the custom-points value
      // itself didn't change from the DB (and therefore has no history row).
      const POINTS_TO_SWITCH: Record<string, string> = {
        pgIntangiblesCustomPoints: "pgIntangiblesSwitch",
        pgOverrideCustomPoints: "pgOverrideSwitch",
        corpIntangiblesCustomPoints: "intangiblesSwitch",
        corpOverrideCustomPoints: "overrideSwitch",
        preferredVendorPoints: "preferredVendorSwitch",
      };
      const notesWithSwitches: Record<string, string> = { ...scoreNotes };
      for (const [pointsKey, switchKey] of Object.entries(POINTS_TO_SWITCH)) {
        if (scoreNotes[pointsKey]) {
          notesWithSwitches[switchKey] = scoreNotes[pointsKey];
        }
      }
      try {
        await axios.post(
          `${serverConfig.api_base_url}underwriting-engine/save-application`,
          {
            applicationNumber: currentInputs.applicationNumber,
            formData: encodeSentinels(currentInputs),
            actor,
            changedFields: Object.keys(changedFields).length > 0 ? changedFields : undefined,
            notes: notesWithSwitches,
          },
          { headers: { "Content-Type": "application/json" } }
        );
      } catch (err) {
        console.error("Failed to save score notes:", err);
      }
    }
    setInitialValues(currentInputs);
    scoreStepDirtyRef.current = false;
    setShowScoreNotesModal(false);
    setScoreNotes({});
    setScoreNeedsReason([]);
    setCurrentStep("Review");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleApplicationCreditHold = async () => {
    setIsSubmittingCreditHold(true);
    try {
      const lockOk = await acquireLoanLock(String(inputs.applicationNumber));
      if (!lockOk) return;
      const token = await UserService.ensureFreshToken();
      await axios.post(
        `${serverConfig.api_base_url}underwriting-engine/decision`,
        {
          id: null,
          decision: "CreditHold",
          applicationNumber: Number(inputs.applicationNumber || 0),
          formData: encodeSentinels(inputs),
          token,
          hold_reason: "",
        },
        { headers: { "Content-Type": "application/json" } }
      );
      setCurrentStep("Loans");
    } catch (err) {
      console.error("Error setting credit hold from Application step:", err);
    } finally {
      setIsSubmittingCreditHold(false);
    }
  };

  const handleExposureAccept = async () => {
    const actor = UserService.getUsername() || UserService.getEmail() || null;
    try {
      // Use initialValues (last-saved state) as the base so that any unsaved
      // field modifications in inputs are not persisted here without notes.
      // Only the acknowledgement flag changes.
      await axios.post(
        `${serverConfig.api_base_url}underwriting-engine/save-application`,
        {
          applicationNumber: inputs.applicationNumber,
          formData: encodeSentinels({ ...initialValues, customerExposureAcknowledged: true }),
          actor,
          notes: {},
        },
        { headers: { "Content-Type": "application/json" } }
      );
    } catch (err) {
      console.error("Failed to save customer exposure acknowledgment:", err);
    }
    setInputs((prev) => ({ ...prev, customerExposureAcknowledged: true }));
    setCustomerExposureChecked(true);
    setShowExposureModal(false);
    setExposureChecked(false);
    setCurrentStep("Score");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const checkCriteria = () => {
    if (Number(inputs.loanAmount || 0) > LOAN_AMOUNT_MAX) return true;

    const failures: any[] = [];

    // Reset all rule overrides to 0
    setInputs((prev) => ({
      ...prev,
      requiresPersonalGuarantor: 0,
      corpThreeOfFourCriteriaFailed: 0,
      corpOneOfTwoCriteriaFailed: 0,
      loanShortfallSupportFailed: 0,
      bankStatementCriteriaFailed: 0,
    }));

    // Modified application-data fields — require a reason for each (mandatory prompt).
    if (Object.keys(modifiedFields).length > 0) {
      const modifiedFieldsList = Object.entries(modifiedFields).map(
        ([field, { label, previousVal, newVal }]: [string, any]) => ({
          label: label || field,
          value: `${previousVal} → ${newVal || "null"}`,
          isModified: true,
          fieldKey: field,
        })
      );
      failures.push(failCriteria("The following fields have been modified:", modifiedFieldsList));
    }

    // Rule A
    const timeInBusiness = Number(inputs.timeInBusiness) || 0;
    const noOfGuarantors = Number(inputs.noOfGuarantors) || 0;
    if (timeInBusiness < 4 && noOfGuarantors < 1) {
      setInputs((prev) => ({ ...prev, requiresPersonalGuarantor: 1 }));
      failures.push(
        failCriteria("Time in business < 4 years, need Personal Guarantor.", [
          {
            label: "Time in Business",
            value: inputs.timeInBusiness || 0,
            isValid: timeInBusiness >= 4,
          },
          {
            label: "No. of Guarantors",
            value: inputs.noOfGuarantors || 0,
            isValid: noOfGuarantors >= 1,
          },
        ])
      );
    }

    const loanAmount = Number(inputs.loanAmount) || 0;
    if (loanAmount > 100000) {
      if (inputs.useCorpSwitch) {
        // Rule 1
        const checks1 = [
          {
            label: "Employees ≥ 50",
            value: inputs.numberOfEmployees || 0,
            isValid: Number(inputs.numberOfEmployees) >= 50,
          },
          {
            label: "Time in Business ≥ 10",
            value: inputs.timeInBusiness || 0,
            isValid: Number(inputs.timeInBusiness) >= 10,
          },
          {
            label: "Paydex ≥ 70",
            value: inputs.paydex || 0,
            isValid: Number(inputs.paydex) >= 70,
          },
          {
            label: "Trades of PayNet ≥ 5",
            value: inputs.pnNoOfTradeExperiences || 0,
            isValid: Number(inputs.pnNoOfTradeExperiences) >= 5,
          },
        ];
        if (checks1.filter((c) => c.isValid).length < 3) {
          setInputs((prev) => ({ ...prev, corpThreeOfFourCriteriaFailed: 1 }));
          failures.push(failCriteria("Must fulfil at least 3 of these 4 conditions", checks1));
        }

        // Rule 2
        const checks2 = [
          {
            label: "D&B High Credit ≥ Loan Amount",
            value: `${inputs.highCredit || 0} / ${inputs.loanAmount}`,
            isValid: Number(inputs.highCredit) >= Number(inputs.loanAmount),
          },
          {
            label: "PN High Credit ≥ Loan Amount",
            value: `${inputs.pnHighCredit || 0} / ${inputs.loanAmount}`,
            isValid: Number(inputs.pnHighCredit) >= Number(inputs.loanAmount),
          },
        ];
        if (!checks2.some((c) => c.isValid)) {
          setInputs((prev) => ({ ...prev, corpOneOfTwoCriteriaFailed: 1 }));
          failures.push(failCriteria("Must fulfil 1 of these 2 conditions (Corp Only)", checks2));
        }

        // Rule 3 (Corp only) — high-credit shortfall support: fires only when NEITHER
        // the D&B nor the PayNet High Credit covers the loan (i.e. Rule 2's 1-of-2 unmet).
        // Scoped inside the useCorpSwitch branch: PG loans must never raise a
        // credit-policy override.
        const highCredit = Number(inputs.highCredit) || 0;
        const pnHighCredit = Number(inputs.pnHighCredit) || 0;
        if (highCredit < loanAmount && pnHighCredit < loanAmount) {
          const checks3 = [
            {
              label: "No. of Guarantors > 0",
              value: inputs.noOfGuarantors || 0,
              isValid: noOfGuarantors > 0,
            },
            {
              label: "FICO > 725",
              value: inputs.udf_fico_average === "" ? "—" : inputs.udf_fico_average || 0,
              isValid: inputs.udf_fico_average === "" || Number(inputs.udf_fico_average) > 725,
            },
            {
              label: "Real Estate Status",
              value: inputs.realEstateStatus || "—",
              isValid: !!inputs.realEstateStatus,
            },
            {
              label: "Availability > 75%",
              value: inputs.totalRevolvingAvailable || 0,
              isValid: Number(inputs.totalRevolvingAvailable) > 75,
            },
          ];
          if (!checks3.every((c) => c.isValid)) {
            setInputs((prev) => ({ ...prev, loanShortfallSupportFailed: 1 }));
            failures.push(
              failCriteria("High Credit < Loan Amount, must fulfil all these conditions", checks3)
            );
          }
        }

        // Rule 4 (Corp only) — over $250k, both bank-statement criteria for the loan's
        // band must be yes.
        const bankCriteria = bankStatementCriteria(loanAmount);
        if (bankCriteria) {
          const checks4 = bankCriteria.map(({ field, label }) => ({
            label,
            value: yesNo((inputs as any)[field]) || "—",
            isValid: yesNo((inputs as any)[field]) === "Yes",
          }));
          if (!checks4.every((c) => c.isValid)) {
            setInputs((prev) => ({ ...prev, bankStatementCriteriaFailed: 1 }));
            failures.push(failCriteria("Bank statements (3 months) must meet both minimums", checks4));
          }
        }
      }
    }

    if (failures.length > 0) {
      console.log("Criteria check failed, showing modal with failures:", failures);
      setFailedRules(failures);
      setCriteriaMessage(failures[0]);
      setShowCriteriaModal(true);
      return false;
    }
    
    console.log("Criteria check passed, no failures");

    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Move from Score to Review without criteria popup
    if (currentStep === "Score") {
      setCurrentStep("Review");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      console.log(inputs);
    }
  };

  const microTicket = isMicroTicket(inputs.loanAmount);
  // Only the bank-statement pair the deal's bank check uses (corp deals over $250k).
  const bankFieldsShown = (inputs.useCorpSwitch && bankStatementCriteria(inputs.loanAmount)) || [];

  return (
    <div className="space-y-6">

      {isLoading && (
        <div className="fixed inset-0 z-50 bg-black/50">
          <IntroAnimation splashlogo={splashlogo} imgsrc={logo} />
        </div>
      )}

      {showAlert && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{alertMsg}</AlertDescription>
        </Alert>
      )}

      {currentStep === "Loans" && (
        <div className="space-y-4">
          <div className="flex items-center justify-end">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label>Search:</Label>
                <Input
                  type="text"
                  placeholder="Application Number"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setCurrentPage(1);
                      fetchData(searchTerm, statusFilter, 1);
                    }
                  }}
                  className="w-[200px]"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label>Status:</Label>
                <Select value={statusFilter || "all"} onValueChange={(val) => {
                  const newStatus = val === "all" ? "" : val;
                  setStatusFilter(newStatus);
                  setCurrentPage(1);
                  fetchData(searchTerm, newStatus, 1);
                }}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New">Credit Review</SelectItem>
                    <SelectItem value="Data Entry">Data Entry</SelectItem>
                    <SelectItem value="Credit Hold">Credit Hold</SelectItem>
                    <SelectItem value="Hold">Hold</SelectItem>
                    <SelectItem value="Referred">Referred</SelectItem>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                    <SelectItem value="Withdrawn">Withdrawn</SelectItem>
                    <SelectItem value="Syndicate">Syndicate</SelectItem>
                    <SelectItem value="Unmapped">Unmapped</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Application Number</TableHead>
                  <TableHead>Business Name</TableHead>
                  <TableHead>Office Number</TableHead>
                  <TableHead>Office Name</TableHead>
                  <TableHead>Submit Date</TableHead>
                  <TableHead>Loan Amount</TableHead>
                  <TableHead>Customer Exposure</TableHead>
                  <TableHead>Asset Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      No loans found.
                    </TableCell>
                  </TableRow>
                ) : (
                  tableData.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.applicationNumber}</TableCell>
                      <TableCell>{row.businessName || "—"}</TableCell>
                      <TableCell>{row.officeNumber}</TableCell>
                      <TableCell>{row.officeName}</TableCell>
                      <TableCell>{row.submitDate}</TableCell>
                      <TableCell>{row.loanAmount}</TableCell>
                      <TableCell>
                        {row.customerExposure != null
                          ? row.customerExposure.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : "0"}
                      </TableCell>
                      <TableCell>{row.assetType}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getStatusClassName(row.status)}>
                          {getStatusLabel(row.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          onClick={() => handleLoanProcess(row.applicationNumber, row.status, row.officeNumber)}
                        >
                          {isTerminalStatus(row.status) ? "View Loan" : "Process Loan"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => {
                  const prev = currentPage - 1;
                  setCurrentPage(prev);
                  fetchData(searchTerm, statusFilter, prev);
                }}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => {
                  const next = currentPage + 1;
                  setCurrentPage(next);
                  fetchData(searchTerm, statusFilter, next);
                }}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {(currentStep === "Application" ||
        currentStep === "Score" ||
        currentStep === "Review" ||
        currentStep === "Underwrite") && (
        <div className="space-y-6">
        <div
          id="application-details"
          className={cn(currentStep === "Underwrite" && "hidden")}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
          {isLockedStatus && (
            <Alert variant="default" className="border-red-600 bg-red-950/30">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <AlertDescription className="text-red-300">
                This application is <strong>{loanStatus}</strong> and is view-only. No changes can be made.
              </AlertDescription>
            </Alert>
          )}
          {(currentStep === "Application" || currentStep === "Underwrite") && (
            <div className="space-y-6">
              {/* Main Loan Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="label-athena">Loan Application Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="label-athena">Application Number</Label>
                  <Input
                    type="number"
                    placeholder="Enter here"
                    name="applicationNumber"
                    value={sanitizeDisplay(inputs.applicationNumber) || 0}
                    onChange={handleChange}
                    disabled
                    className={errors.applicationNumber ? "border-destructive" : ""}
                  />
                  {errors.applicationNumber && (
                    <p className="text-sm text-destructive">{errors.applicationNumber}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="label-athena">Name of Business</Label>
                  <Input
                    type="text"
                    placeholder="Enter here"
                    name="nameOfBusiness"
                    value={inputs.nameOfBusiness || ""}
                    onChange={handleChange}
                    disabled
                  />
                </div>

                <div className="space-y-2">
                  <Label className="label-athena">Office Name</Label>
                  <Select
                    name="officeName"
                    value={inputs.officeName || ""}
                    onValueChange={(value) => handleChange({ target: { name: "officeName", value } } as any)}
                    disabled
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="-- Select --" />
                    </SelectTrigger>
                    <SelectContent>
                      {officeName.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="label-athena">Loan Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground">$</span>
                    <Input
                      type="number"
                      placeholder="0"
                      name="loanAmount"
                      value={sanitizeDisplay(inputs.loanAmount) || 0}
                      onChange={handleChange}
                      disabled
                      className={`pl-7 ${errors.loanAmount ? "border-destructive" : ""}`}
                    />
                  </div>
                  {errors.loanAmount && (
                    <p className="text-sm text-destructive">{errors.loanAmount}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="label-athena">Asset Type</Label>
                  <Select
                    name="assetType"
                    value={inputs.assetType || ""}
                    onValueChange={(value) => handleChange({ target: { name: "assetType", value } } as any)}
                    disabled
                  >
                    <SelectTrigger className={errors.assetType ? "border-destructive" : ""}>
                      <SelectValue placeholder="-- Select --" />
                    </SelectTrigger>
                    <SelectContent>
                      {assetTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.assetType && (
                    <p className="text-sm text-destructive">{errors.assetType}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="label-athena">City</Label>
                  <Input
                    type="text"
                    placeholder="Enter here"
                    name="city"
                    value={inputs.city || ""}
                    onChange={handleChange}
                    disabled
                  />
                </div>

                <div className="space-y-2">
                  <Label className="label-athena">Customer Exposure</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground">$</span>
                    <Input
                      type="number"
                      placeholder="0"
                      value={inputs.customerExposure ?? 0}
                      disabled
                      className="pl-7"
                    />
                  </div>
                </div>

                {bankFieldsShown.map(({ field, label }) => (
                  <div key={field} className="space-y-2">
                    <Label className="label-athena">{label}</Label>
                    <Select
                      value={yesNo((inputs as any)[field]) || "_none_"}
                      onValueChange={(value) => {
                        const original = initialValues[field];
                        const picked = value === "_none_" ? "" : value;
                        handleChange({
                          target: {
                            name: field,
                            // Picking the original answer again restores the original value, so it
                            // is not an edit; a new answer is saved as "yes" / "no", or NULL when cleared.
                            value: picked === yesNo(original) ? original : picked.toLowerCase() || null,
                          },
                        } as any);
                      }}
                      // Locked while the API does not carry the column yet (nowhere to save it)
                      disabled={isReadOnly || !isApplicationEditable || (inputs as any)[field] === undefined}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none_">—</SelectItem>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                  </div>
                </CardContent>
              </Card>

              {/* Bureau Details | D&B */}
              <Card id="pdf-dnb-card">
                <CardHeader>
                  <CardTitle className="label-athena">Bureau Details | D&B</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label className="label-athena">Average High Credit</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="avgHighCredit"
                      value={sanitizeDisplay(inputs.avgHighCredit) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">High Credit</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="highCredit"
                      value={sanitizeDisplay(inputs.highCredit) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Paydex</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="paydex"
                      value={sanitizeDisplay(inputs.paydex) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Suits</Label>
                    <Select
                      value={inputs.suits || "_none_"}
                      onValueChange={(value) =>
                        handleChange({
                          target: { name: "suits", value: value === "_none_" ? "" : value },
                        } as any)
                      }
                      disabled={isReadOnly || !isApplicationEditable}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none_">—</SelectItem>
                        <SelectItem value="S">S</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Liens</Label>
                    <Select
                      value={inputs.liens || "_none_"}
                      onValueChange={(value) =>
                        handleChange({
                          target: { name: "liens", value: value === "_none_" ? "" : value },
                        } as any)
                      }
                      disabled={isReadOnly || !isApplicationEditable}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none_">—</SelectItem>
                        <SelectItem value="L">L</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Judgements</Label>
                    <Select
                      value={inputs.judgements || "_none_"}
                      onValueChange={(value) =>
                        handleChange({
                          target: { name: "judgements", value: value === "_none_" ? "" : value },
                        } as any)
                      }
                      disabled={isReadOnly || !isApplicationEditable}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none_">—</SelectItem>
                        <SelectItem value="J">J</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Start Year</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="startYear"
                      value={sanitizeDisplay(inputs.startYear) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Number of Trade Lines</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="noOfTradeExperiences"
                      value={sanitizeDisplay(inputs.noOfTradeExperiences) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Bankruptcy</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="bankruptcy"
                      value={sanitizeDisplay(inputs.bankruptcy) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">SIC Code</Label>
                    <Input
                      type="text"
                      placeholder="Enter here"
                      name="standardCode"
                      value={inputs.standardCode || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Commercial Credit Score</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="commercialCreditScore"
                      value={sanitizeDisplay(inputs.commercialCreditScore) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bureau Details | Experian */}
              <Card id="pdf-experian-card">
                <CardHeader>
                  <CardTitle className="label-athena">Bureau Details | Experian</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label className="label-athena">Birth Year</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="birthyear"
                      value={sanitizeDisplay(inputs.birthyear) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Vantage</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="vantage"
                      value={sanitizeDisplay(inputs.vantage) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Number of Satisfactory Accounts</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="noOfSatisfactoryAccounts"
                      value={sanitizeDisplay(inputs.noOfSatisfactoryAccounts) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Number of Inquiries</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="noOfInquiries"
                      value={sanitizeDisplay(inputs.noOfInquiries) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Was Delinquent</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="noOfTotalDelinquencies"
                      value={sanitizeDisplay(inputs.noOfTotalDelinquencies) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Number of Delinquencies</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="noOfDelinquencies"
                      value={sanitizeDisplay(inputs.noOfDelinquencies) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">FICO Score</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="udf_fico"
                      value={sanitizeDisplay(inputs.udf_fico) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Average FICO Score</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="udf_fico_average"
                      value={sanitizeDisplay(inputs.udf_fico_average) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Max FICO Score</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="udf_fico_max"
                      value={sanitizeDisplay(inputs.udf_fico_max) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Total Revolving Balance</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="totalRevolvingBalance"
                      value={sanitizeDisplay(inputs.totalRevolvingBalance) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Total Revolving Available</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="totalRevolvingAvailable"
                      value={sanitizeDisplay(inputs.totalRevolvingAvailable) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Real Estate Balance</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="realStateBalance"
                      value={sanitizeDisplay(inputs.realStateBalance, 1)}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Real Estate Status</Label>
                    <select
                      name="realEstateStatus"
                      value={inputs.realEstateStatus || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select...</option>
                      <option value="Paid Off">Paid Off</option>
                      <option value="HELOC">HELOC</option>
                    </select>
                  </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bureau Details | PayNet */}
              <Card id="pdf-paynet-card">
                <CardHeader>
                  <CardTitle className="label-athena">Bureau Details | PayNet</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label className="label-athena">PN Master Score</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="pnMasterScore"
                      value={sanitizeDisplay(inputs.pnMasterScore) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">High Credit</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="pnHighCredit"
                      value={sanitizeDisplay(inputs.pnHighCredit) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Number of Trade Lines</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="pnNoOfTradeExperiences"
                      value={sanitizeDisplay(inputs.pnNoOfTradeExperiences) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Pass Score</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="udf_pass_score"
                      value={sanitizeDisplay(inputs.udf_pass_score) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  </div>
                </CardContent>
              </Card>

              {/* Loan Application | LeaseWorks */}
              <Card id="pdf-leaseworks-card" className="!mt-[5.5rem]">
                <CardHeader>
                  <CardTitle className="label-athena">Loan Application | LeaseWorks</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label className="label-athena">Personal Guarantors</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="noOfGuarantors"
                      value={sanitizeDisplay(inputs.noOfGuarantors) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Corporate Guarantors</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="noOfCorpGuarantors"
                      value={sanitizeDisplay(inputs.noOfCorpGuarantors) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Time In Business</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="timeInBusiness"
                      value={sanitizeDisplay(inputs.timeInBusiness) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Number of Employees</Label>
                    <Input
                      type="number"
                      placeholder="Enter here"
                      name="numberOfEmployees"
                      value={sanitizeDisplay(inputs.numberOfEmployees) || ""}
                      onChange={handleChange}
                      disabled={isReadOnly || !isApplicationEditable}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Approved Amount</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground">$</span>
                      <Input
                        type="number"
                        placeholder="0"
                        name="approvedAmount"
                        value={sanitizeDisplay(inputs.approvedAmount) ?? ""}
                        onChange={handleChange}
                        disabled={isReadOnly}
                        className="pl-7"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-athena">Yield</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        placeholder="Enter here"
                        name="yield"
                        value={inputs.yield != null && inputs.yield !== "" ? (Number(inputs.yield) * 100).toFixed(2) : ""}
                        onChange={handleChange}
                        disabled={true}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                    </div>
                  </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {(currentStep === "Score" ||
            currentStep === "Review" ||
            currentStep === "Underwrite") && (
            <div id="pdf-score-section" className="space-y-6">
              {Number(inputs.loanAmount || 0) > LOAN_AMOUNT_MAX && (currentStep === "Score" || currentStep === "Review") ? (
                <Card>
                  <CardContent className="py-6">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-primary shrink-0" />
                      <p className="text-sm font-medium text-primary">
                        Deals over $500,000 cannot be processed in Athena. This application is out of scope.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
              <div id="pdf-pg-wrapper" className="space-y-6">
              {/* Personal Guarantor (PG) Details */}
              <div id="pdf-pg-scoring">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <CardTitle className="label-athena">Personal Guarantor (PG) Details</CardTitle>
                    <Switch
                      checked={inputs.usePGSwitch || false}
                      onCheckedChange={(checked) =>
                        handleChange({
                          target: { name: "usePGSwitch", type: "checkbox", checked },
                        } as any)
                      }
                      disabled={isReadOnly || Number(inputs.noOfGuarantors) <= 0 || inputs.anyMajorDerog || ((inputs.udf_fico === "" || inputs.udf_fico == null) && (inputs.udf_fico_average === "" || inputs.udf_fico_average == null) && (inputs.udf_fico_max === "" || inputs.udf_fico_max == null))}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  {inputs.usePGSwitch && (
                    <>
                    <div className={inputs.anyMajorDerog ? "pointer-events-none opacity-50" : ""}>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label className="label-athena">Time in Business</Label>
                      <Input
                        type="number"
                        placeholder="Enter here"
                        name="pgTimeInBusiness"
                        value={sanitizeDisplay(inputs.pgTimeInBusiness) || ""}
                        onChange={handleChange}
                        disabled={
                          isReadOnly ||
                          !inputs.usePGSwitch ||
                          (activeStep === "Score")
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-athena">FICO Score</Label>
                      <Input
                        type="number"
                        placeholder="Enter here"
                        name="pgFicoScore"
                        value={sanitizeDisplay(inputs.pgFicoScore) || ""}
                        onChange={handleChange}
                        disabled={
                          isReadOnly ||
                          !inputs.usePGSwitch ||
                          (activeStep === "Score")
                        }
                      />
                    </div>
                    <div className="space-y-2"></div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Strong Payment History</Label>
                        <CriteriaInfo label="Strong Payment History" />
                        <Switch
                          checked={inputs.pgStrongPaymentHistorySwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "pgStrongPaymentHistorySwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.usePGSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          +{inputs.pgStrongPaymentHistorySwitchPoints}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Acceptable Payment History</Label>
                        <CriteriaInfo label="Acceptable Payment History" />
                        <Switch
                          checked={inputs.pgAcceptableCreditHistorySwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "pgAcceptableCreditHistorySwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.usePGSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          +{inputs.pgAcceptableCreditHistorySwitchPoints}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Strong Comparable Credit</Label>
                        <CriteriaInfo label="Strong Comparable Credit" />
                        <Switch
                          checked={inputs.pgStrongComparibleCreditSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "pgStrongComparibleCreditSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.usePGSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          +{inputs.pgStrongComparibleCreditSwitchPoints}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Acceptable Comparable Credit</Label>
                        <CriteriaInfo label="Acceptable Comparable Credit" />
                        <Switch
                          checked={inputs.pgAcceptableComparibleCreditSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "pgAcceptableComparibleCreditSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.usePGSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          +{inputs.pgAcceptableComparibleCreditSwitchPoints}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Preferred Collateral</Label>
                        <CriteriaInfo label="Preferred Collateral" />
                        <Switch
                          checked={inputs.pgPreferredCollateralSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "pgPreferredCollateralSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.usePGSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          +{inputs.pgPreferredCollateralSwitchPoints}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Soft Collateral</Label>
                        <CriteriaInfo label="Soft Collateral" />
                        <Switch
                          checked={inputs.pgSoftCollateralSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "pgSoftCollateralSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.usePGSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          {inputs.pgSoftCollateralSwitchPoints}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Intangibles</Label>
                        <CriteriaInfo label="Intangibles" />
                        <Switch
                          checked={inputs.pgIntangiblesSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "pgIntangiblesSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.usePGSwitch}
                        />
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          className="h-8 w-16"
                          name="pgIntangiblesCustomPoints"
                          value={sanitizeDisplay(inputs.pgIntangiblesCustomPoints) ?? ""}
                          onChange={handleChange}
                          disabled={
                            isReadOnly ||
                            !inputs.usePGSwitch ||
                            !inputs.pgIntangiblesSwitch
                          }
                        />
                        <span className="text-sm text-muted-foreground">
                          +{Number(inputs.pgIntangiblesCustomPoints) || 0}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Override</Label>
                        <Switch
                          checked={inputs.pgOverrideSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "pgOverrideSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.usePGSwitch}
                        />
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          className="h-8 w-16"
                          name="pgOverrideCustomPoints"
                          value={sanitizeDisplay(inputs.pgOverrideCustomPoints) ?? ""}
                          onChange={handleChange}
                          disabled={
                            isReadOnly ||
                            !inputs.usePGSwitch ||
                            !inputs.pgOverrideSwitch
                          }
                        />
                        <span className="text-sm text-muted-foreground">
                          +{Number(inputs.pgOverrideCustomPoints) || 0}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Advance Payment</Label>
                        <Switch
                          checked={inputs.pgAdvancePaymentSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "pgAdvancePaymentSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.usePGSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          {inputs.pgAdvancePaymentSwitch ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Vendor Clear</Label>
                        <Switch
                          checked={inputs.pgVendorClearSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "pgVendorClearSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.usePGSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          {inputs.pgVendorClearSwitch ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Vendor Support</Label>
                        <CriteriaInfo label="Vendor Support" />
                        <Switch
                          checked={inputs.pgVendorSupportSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "pgVendorSupportSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.usePGSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          {inputs.pgVendorSupportSwitch ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>

                    <div />

                    {/* PG Score and Rating Card */}
                    {inputs.usePGSwitch && (inputs.pgScore || inputs.pgRating) && (
                      <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'hsl(216, 11%, 12%)' }}>
                        <div className="mb-3">
                          <CardTitle className="label-athena text-lg">Personal Guarantor Results</CardTitle>
                        </div>
                        {inputs.anyMajorDerog ? (
                          <div className="text-3xl font-bold" style={{ color: "#ff0000" }}>Declined</div>
                        ) : (
                          <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <Label className="text-sm text-muted-foreground">PG Score</Label>
                              <div className="text-3xl font-bold text-primary">
                                {inputs.pgScore || "N/A"}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm text-muted-foreground">PG Rating</Label>
                              <div className="text-3xl font-bold text-primary">
                                {inputs.pgRating || "N/A"}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3 mt-12">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Major Derog</Label>
                        <Switch
                          checked={inputs.anyMajorDerog || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "anyMajorDerog", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly}
                        />
                      </div>
                    </div>
                  </div>
                  </>
                )}
                </CardContent>
              </Card>
              </div>

              {/* Corp Details */}
              <div id="pdf-corp-scoring">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <CardTitle className="label-athena">Corp Details</CardTitle>
                    <Switch
                      checked={inputs.useCorpSwitch || false}
                      onCheckedChange={(checked) =>
                        handleChange({
                          target: { name: "useCorpSwitch", type: "checkbox", checked },
                        } as any)
                      }
                      disabled={isReadOnly || inputs.anyMajorDerog}
                    />
                    {microTicket && <Badge variant="outline">Micro-ticket (up to $50,000)</Badge>}
                  </div>
                </CardHeader>
                <CardContent>
                  {inputs.useCorpSwitch && (
                    <>
                    <div className={inputs.anyMajorDerog ? "pointer-events-none opacity-50" : ""}>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label className="label-athena">Time in Business</Label>
                      <Input
                        type="number"
                        placeholder="Enter here"
                        name="corpTimeInBusiness"
                        value={sanitizeDisplay(inputs.corpTimeInBusiness) || ""}
                        onChange={handleChange}
                        disabled={
                          isReadOnly ||
                          !inputs.useCorpSwitch ||
                          (activeStep === "Score")
                        }
                      />
                      <span className="text-xs text-muted-foreground">
                        Points: {inputs.corpTimeInBusinessPoints}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-athena">Number of Employees</Label>
                      <Input
                        type="number"
                        placeholder="Enter here"
                        name="corpNumberOfEmployees"
                        value={sanitizeDisplay(inputs.corpNumberOfEmployees) || ""}
                        onChange={handleChange}
                        disabled={
                          isReadOnly ||
                          !inputs.useCorpSwitch ||
                          (activeStep === "Score")
                        }
                      />
                      <span className="text-xs text-muted-foreground">
                        Points: {inputs.corpNumberOfEmployeesPoints}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-athena">{microTicket ? "High Credit (D&B/PayNet)" : "High Credit"}</Label>
                      <Input
                        type="number"
                        placeholder="Enter here"
                        name="corpHighCredit"
                        value={sanitizeDisplay(inputs.corpHighCredit) || ""}
                        onChange={handleChange}
                        disabled={
                          isReadOnly ||
                          !inputs.useCorpSwitch ||
                          (activeStep === "Score")
                        }
                      />
                      <span className="text-xs text-muted-foreground">
                        Points: {inputs.corpHighCreditPoints}
                        {microTicket &&
                          ` · ${Math.round(((Number(inputs.corpHighCredit) || 0) / Number(inputs.loanAmount)) * 100)}% of loan amount`}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-athena">Paydex</Label>
                      <Input
                        type="number"
                        placeholder="Enter here"
                        name="paydex"
                        value={sanitizeDisplay(inputs.paydex) || ""}
                        disabled={true}
                      />
                      <span className="text-xs text-muted-foreground">
                        Points: {inputs.corpPaydexPoints}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-athena">{microTicket ? "Trade Lines (D&B/PayNet)" : "Trade Lines"}</Label>
                      <Input
                        type="number"
                        placeholder="Enter here"
                        name="corpTradeLines"
                        value={sanitizeDisplay(inputs.corpTradeLines) || ""}
                        onChange={handleChange}
                        disabled={
                          isReadOnly ||
                          !inputs.useCorpSwitch ||
                          (activeStep === "Score")
                        }
                      />
                      <span className="text-xs text-muted-foreground">
                        Points: {inputs.corpTradeLinesPoints}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-athena">Suits/Liens/Judgements</Label>
                      <Select
                        value={inputs.suitsLiensJudgements || ""}
                        onValueChange={(value) =>
                          handleChange({
                            target: { name: "suitsLiensJudgements", value },
                          } as any)
                        }
                        disabled={isReadOnly || !inputs.useCorpSwitch}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="-- Select --" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="material">Material</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-xs text-muted-foreground">
                        Points: {inputs.suitsLiensJudgementsPoints}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">History Clear</Label>
                        <Switch
                          checked={inputs.historyClearSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "historyClearSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          +{inputs.historyClearPoints}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">UCC Filings</Label>
                        <Switch
                          checked={inputs.uccFilingsSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "uccFilingsSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          +{inputs.uccFilingsPoints}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">High Risk Industry</Label>
                        <Switch
                          checked={inputs.highRiskIndustrySwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "highRiskIndustrySwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          {inputs.highRiskIndustryPoints}
                        </span>
                      </div>
                    </div>
                    {microTicket ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Preferred Vendor</Label>
                        <Switch
                          checked={inputs.preferredVendorSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "preferredVendorSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <Input
                          type="number"
                          min={0}
                          max={2}
                          step={1}
                          className="h-8 w-16"
                          name="preferredVendorPoints"
                          value={sanitizeDisplay(inputs.preferredVendorPoints) ?? ""}
                          onChange={handleChange}
                          disabled={
                            isReadOnly ||
                            !inputs.useCorpSwitch ||
                            !inputs.preferredVendorSwitch
                          }
                        />
                        <span className="text-sm text-muted-foreground">
                          +{Number(inputs.preferredVendorPoints) || 0}
                        </span>
                      </div>
                    </div>
                    ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Comparable Lease</Label>
                        <Switch
                          checked={inputs.comparableLeaseSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "comparableLeaseSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          +{inputs.comparableLeasePoints}
                        </span>
                      </div>
                    </div>
                    )}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Majority Equity Single Ownership</Label>
                        <Switch
                          checked={inputs.majorityEquitySingleOwnershipSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "majorityEquitySingleOwnershipSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          {inputs.majorityEquitySingleOwnershipPoints}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Preferred Collateral</Label>
                        <CriteriaInfo label="Preferred Collateral" />
                        <Switch
                          checked={inputs.preferredCollateralSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "preferredCollateralSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          +{inputs.preferredCollateralPoints}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Soft Collateral</Label>
                        <CriteriaInfo label="Soft Collateral" />
                        <Switch
                          checked={inputs.softCollateralSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "softCollateralSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          {inputs.softCollateralPoints}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Intangibles</Label>
                        <CriteriaInfo label="Intangibles" />
                        <Switch
                          checked={inputs.intangiblesSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "intangiblesSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          className="h-8 w-16"
                          name="corpIntangiblesCustomPoints"
                          value={sanitizeDisplay(inputs.corpIntangiblesCustomPoints) ?? ""}
                          onChange={handleChange}
                          disabled={
                            isReadOnly ||
                            !inputs.useCorpSwitch ||
                            !inputs.intangiblesSwitch
                          }
                        />
                        <span className="text-sm text-muted-foreground">
                          +{Number(inputs.corpIntangiblesCustomPoints) || 0}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Override</Label>
                        <Switch
                          checked={inputs.overrideSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "overrideSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          className="h-8 w-16"
                          name="corpOverrideCustomPoints"
                          value={sanitizeDisplay(inputs.corpOverrideCustomPoints) ?? ""}
                          onChange={handleChange}
                          disabled={
                            isReadOnly ||
                            !inputs.useCorpSwitch ||
                            !inputs.overrideSwitch
                          }
                        />
                        <span className="text-sm text-muted-foreground">
                          +{Number(inputs.corpOverrideCustomPoints) || 0}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Advance Payment</Label>
                        <Switch
                          checked={inputs.corpAdvancePaymentSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "corpAdvancePaymentSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          {inputs.corpAdvancePaymentSwitch ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Vendor Clear</Label>
                        <Switch
                          checked={inputs.corpVendorClearSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "corpVendorClearSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          {inputs.corpVendorClearSwitch ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Vendor Support</Label>
                        <CriteriaInfo label="Vendor Support" />
                        <Switch
                          checked={inputs.corpVendorSupportSwitch || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "corpVendorSupportSwitch", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly || !inputs.useCorpSwitch}
                        />
                        <span className="text-sm text-muted-foreground">
                          {inputs.corpVendorSupportSwitch ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>

                    {/* Corp Score and Rating Card */}
                    {inputs.useCorpSwitch && (inputs.corpScore || inputs.corpRating) && (
                      <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'hsl(216, 11%, 12%)' }}>
                        <div className="mb-3">
                          <CardTitle className="label-athena text-lg">Corp Results</CardTitle>
                        </div>
                        {inputs.anyMajorDerog ? (
                          <div className="text-3xl font-bold" style={{ color: "#ff0000" }}>Declined</div>
                        ) : (
                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label className="text-sm text-muted-foreground">Corp Score</Label>
                            <div className="text-3xl font-bold text-primary">
                              {inputs.corpScore || "N/A"}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm text-muted-foreground">Corp Rating</Label>
                            <div className="text-3xl font-bold text-primary">
                              {inputs.corpRating || "N/A"}
                            </div>
                          </div>
                        </div>
                        )}
                      </div>
                    )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3 mt-12">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="label-athena">Major Derog</Label>
                        <Switch
                          checked={inputs.anyMajorDerog || false}
                          onCheckedChange={(checked) =>
                            handleChange({
                              target: { name: "anyMajorDerog", type: "checkbox", checked },
                            } as any)
                          }
                          disabled={isReadOnly}
                        />
                      </div>
                    </div>
                  </div>
                  </>
                )}
                </CardContent>
              </Card>
              </div>
              </div>
              )}
            </div>
          )}

        </form>
        </div>

        {/* Credit Hold button — rendered into the footer's extra slot via portal */}
        {footerPortalEl &&
         currentStep === "Application" &&
         Number(inputs.loanAmount || 0) <= LOAN_AMOUNT_MAX &&
         Number(inputs.applicationNumber || 0) > 0 &&
         !isLockedStatus &&
         !isHoldStatus(loanStatus) &&
          createPortal(
            <Button
              type="button"
              variant="outline"
              disabled={isSubmittingCreditHold}
              onClick={handleApplicationCreditHold}
              className="px-6 py-2 border border-foreground text-foreground bg-card hover:bg-foreground hover:text-background hover:border-foreground disabled:opacity-50 font-medium"
            >
              <PauseCircle className="h-4 w-4 mr-1" />
              Credit Hold
            </Button>,
            footerPortalEl
          )}

        {currentStep === "Underwrite" && (
          <Underwrite
            applicationInputs={inputs}
            originalBackendData={originalBackendData}
            setCurrentStep={setCurrentStep}
            loanStatus={loanStatus}
            userCreditLimit={userCreditLimit}
            onAfterDecision={() => fetchData("", "New", 1)}
            onLockError={(msg) => { setAlertMsg(msg); setShowAlert(true); }}
            onSessionTaken={openSessionTakenModal}
          />
        )}
        </div>
      )}

      {/* Lock Takeover Modal (Admin only) */}
      <Dialog open={showTakeoverModal} onOpenChange={(open) => {
        if (!open) {
          setShowTakeoverModal(false);
          setTakeoverLockedBy("");
          pendingTakeoverLoanRef.current = null;
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Loan Currently Locked</DialogTitle>
            <DialogDescription>
              <strong>{takeoverLockedBy}</strong> currently has this application locked.
              Do you want to take over and continue processing?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="text-white" onClick={() => {
              setShowTakeoverModal(false);
              setTakeoverLockedBy("");
              pendingTakeoverLoanRef.current = null;
            }}>
              No, go back
            </Button>
            <Button className="text-black" onClick={async () => {
              const loanId = pendingTakeoverLoanRef.current;
              setShowTakeoverModal(false);
              setTakeoverLockedBy("");
              pendingTakeoverLoanRef.current = null;
              if (loanId) {
                const ok = await takeoverLoanLock(loanId);
                if (ok) void handleLoanProcess(loanId);
              }
            }}>
              Yes, take over
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pipeline Conflict Modal */}
      <Dialog open={showConflictModal} onOpenChange={(open) => {
        if (!open) {
          if (activeLockedLoanRef.current) {
            void releaseActiveLoanLock(activeLockedLoanRef.current);
            activeLockedLoanRef.current = null;
          }
          setShowConflictModal(false);
          setConflictFields([]);
          setConflictSelections({});
          setIsSubmittingConflict(false);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden p-0 !border-0 flex flex-col [&>button]:h-6 [&>button]:w-6 [&>button]:text-foreground/70 [&>button]:hover:text-foreground [&>button>svg]:h-5 [&>button>svg]:w-5 [&>button]:z-10">
          {/* Header */}
          <div className="shrink-0 px-6 pt-6 pb-4 bg-background border-b border-border/50">
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-2">
                <GitMerge className="h-5 w-5 text-primary shrink-0" />
                <DialogTitle className="text-lg label-athena !text-primary">
                  Resolve field conflicts
                </DialogTitle>
              </div>
              <span className="text-xs text-muted-foreground font-medium">Application Number: {pendingAppNumber}</span>
            </div>
            <DialogDescription className="text-xs text-foreground/60 mb-4 ml-7">
              {conflictFields.length} field{conflictFields.length !== 1 ? "s" : ""} changed in LeaseWorks compared to Athena. Pick the value(s) you want to keep.
            </DialogDescription>
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const all: Record<string, "user" | "pipeline"> = {};
                    conflictFields.forEach((cf) => { all[cf.field] = "user"; });
                    setConflictSelections(all);
                  }}
                  className="px-3 py-1 text-xs border border-border rounded text-foreground/80 hover:border-primary hover:text-foreground transition-colors"
                >
                  Keep all edits
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const all: Record<string, "user" | "pipeline"> = {};
                    conflictFields.forEach((cf) => { all[cf.field] = "pipeline"; });
                    setConflictSelections(all);
                  }}
                  className="px-3 py-1 text-xs border border-border rounded text-foreground/80 hover:border-primary hover:text-foreground transition-colors"
                >
                  Keep all pipeline
                </button>
              </div>
              <span className="text-xs font-semibold text-foreground/70">
                {Object.keys(conflictSelections).length} of {conflictFields.length} resolved
              </span>
            </div>
          </div>

          {/* Column headers */}
          <div className="shrink-0 grid grid-cols-[1.3fr_2fr_2fr] px-6 pt-3 pb-0.5 bg-muted/20 border-b border-border/40 items-center">
            <p className="text-xs text-muted-foreground font-medium">Field Names</p>
            <p className="text-xs text-muted-foreground font-medium">Athena Value</p>
            <p className="text-xs text-muted-foreground font-medium">LeaseWorks Value</p>
          </div>

          {/* Scrollable rows */}
          <div className="flex-1 overflow-y-auto">
            {conflictFields.map((cf, idx) => {
              const selected = conflictSelections[cf.field];
              const fmt = (v: any) => {
                if (v === null || v === undefined) return "—";
                if (typeof v === "boolean") return v ? "Yes" : "No";
                return String(v);
              };
              return (
                <div
                  key={cf.field}
                  className={`grid grid-cols-[1.3fr_2fr_2fr] px-6 py-3 items-center ${idx < conflictFields.length - 1 ? "border-b border-border/20" : ""}`}
                >
                  <p className="text-sm text-foreground/70 font-medium pr-4">{cf.label}</p>

                  {/* User edit card */}
                  <div
                    className={`mr-3 p-2.5 cursor-pointer transition-all ${
                      selected === "user"
                        ? "border border-primary bg-primary/10"
                        : "border border-border/40 bg-muted/20 hover:border-primary/40"
                    }`}
                    onClick={() => setConflictSelections((prev) => ({ ...prev, [cf.field]: "user" }))}
                  >
                    <p className="text-xs text-muted-foreground mb-0.5 truncate">{cf.userActor}</p>
                    <p className="text-sm font-bold text-foreground">{fmt(cf.userValue)}</p>
                  </div>

                  {/* Pipeline value card */}
                  <div
                    className={`p-2.5 cursor-pointer transition-all ${
                      selected === "pipeline"
                        ? "border border-primary bg-primary/10"
                        : "border border-border/40 bg-muted/20 hover:border-primary/40"
                    }`}
                    onClick={() => setConflictSelections((prev) => ({ ...prev, [cf.field]: "pipeline" }))}
                  >
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">Pipeline</p>
                      <p className="text-sm font-bold text-foreground">{fmt(cf.pipelineValue)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-border/30 bg-background">
            <button
              type="button"
              onClick={() => {
                if (activeLockedLoanRef.current) {
                  void releaseActiveLoanLock(activeLockedLoanRef.current);
                  activeLockedLoanRef.current = null;
                }
                setShowConflictModal(false);
                setConflictFields([]);
                setConflictSelections({});
              }}
              className="px-5 py-2 border border-border text-foreground font-semibold hover:border-primary text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isSubmittingConflict || conflictFields.some((cf) => !conflictSelections[cf.field])}
              onClick={async () => {
                setIsSubmittingConflict(true);
                const accepted = conflictFields.map((cf) => ({
                  field: cf.field,
                  source: conflictSelections[cf.field],
                  userValue: cf.userValue,
                  pipelineValue: cf.pipelineValue,
                  userActor: cf.userActor,
                }));
                try {
                  await axios.post(
                    `${serverConfig.api_base_url}underwriting-engine/accept-field-values`,
                    {
                      application_number: pendingAppNumber,
                      actor: UserService.getEmail() || "",
                      accepted,
                    },
                    { headers: { "Content-Type": "application/json" } }
                  );
                } catch (err) {
                  console.error("Failed to accept field values:", err);
                }
                setIsSubmittingConflict(false);
                setShowConflictModal(false);
                setConflictFields([]);
                setConflictSelections({});
                // Reload so the form holds the values just kept: later saves send the whole form.
                await handleLoanProcess(String(pendingAppNumber), null, pendingOfficeNumber);
              }}
              className="px-5 py-2 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
            >
              {(() => {
                const editCount = Object.values(conflictSelections).filter((v) => v === "user").length;
                const pipelineCount = Object.values(conflictSelections).filter((v) => v === "pipeline").length;
                const resolved = editCount + pipelineCount;
                return resolved === conflictFields.length
                  ? `Apply ${resolved} changes · ${editCount} edits / ${pipelineCount} pipeline`
                  : `Select all fields to continue`;
              })()}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Criteria Modal */}
      <Dialog open={showCriteriaModal} onOpenChange={(open) => {
        if (!open) {
          setShowCriteriaModal(false);
          setAcceptOverride(false);
          setFieldNotes({});
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0 !border-0 flex flex-col [&>button]:h-6 [&>button]:w-6 [&>button]:text-foreground/70 [&>button]:hover:text-foreground [&>button>svg]:h-5 [&>button>svg]:w-5 [&>button]:z-10">
          {/* Sticky Header */}
          <div className="shrink-0 flex items-center gap-3 px-8 pt-8 pb-4 bg-background border-b border-border/30">
            {criteriaMessage.checks && criteriaMessage.checks.some((c: any) => c.isModified) ? (
              <AlertTriangle className="h-6 w-6 text-warning shrink-0" />
            ) : (
              <AlertCircle className="h-6 w-6 text-destructive shrink-0" />
            )}
            <div>
              <DialogTitle className="text-xl label-athena !text-primary leading-tight">
                {criteriaMessage.checks && criteriaMessage.checks.some((c: any) => c.isModified)
                  ? "Loan Application Alert"
                  : "Credit Policy Alert"}
              </DialogTitle>
              <DialogDescription className="text-sm font-medium text-foreground/70 mt-1 mb-0">
                {criteriaMessage.title}
              </DialogDescription>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-8 pb-8 pt-6">
            <div className="sr-only">{criteriaMessage.title}</div>

          {criteriaMessage.checks && (
            <div className="space-y-4 py-2">
              {criteriaMessage.checks.map((c: any, idx: number) => {
                const fieldKey: string | undefined = c.isModified ? c.fieldKey : undefined;
                const isOptional = fieldKey ? LEASEWORKS_OPTIONAL_FIELDS.has(fieldKey) : false;
                return (
                  <div
                    key={idx}
                    className={cn(
                      "rounded-lg transition-all overflow-hidden",
                      c.isModified
                        ? "bg-warning/5"
                        : c.isValid
                        ? "bg-success/5"
                        : "bg-destructive/5"
                    )}
                  >
                    <div className="flex items-center justify-between p-5">
                      <div className="flex items-center gap-4 flex-1">
                        <div className={cn(
                          "flex items-center justify-center w-10 h-10 rounded-full shrink-0",
                          c.isModified
                            ? "bg-warning/15 text-warning"
                            : c.isValid
                            ? "bg-success/15 text-success"
                            : "bg-destructive/15 text-destructive"
                        )}>
                          {c.isModified ? (
                            <AlertTriangle className="h-5 w-5" />
                          ) : c.isValid ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <XCircle className="h-5 w-5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-base text-foreground mb-1">
                            {c.label}
                          </div>
                          {c.isModified && (
                            <div className="text-xs text-muted-foreground/60 mt-0.5">
                              Field has been modified
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={cn(
                        "ml-4 px-4 py-2 rounded-md font-medium text-sm shrink-0 max-w-[300px] text-right",
                        c.isModified
                          ? "bg-warning/10 text-warning"
                          : c.isValid
                          ? "bg-success/10 text-success"
                          : "bg-destructive/10 text-destructive"
                      )}>
                        {c.value}
                      </div>
                    </div>
                    {c.isModified && fieldKey && (
                      <div className="px-5 pb-4 space-y-1">
                        <Textarea
                          placeholder={isOptional ? "Note (optional)" : "Reason for change (required)"}
                          value={fieldNotes[fieldKey] || ""}
                          onChange={(e) =>
                            setFieldNotes((prev) => ({ ...prev, [fieldKey]: e.target.value.slice(0, 80) }))
                          }
                          maxLength={80}
                          rows={2}
                          className={cn(
                            "text-white placeholder:text-muted-foreground/60",
                            !isOptional && !fieldNotes[fieldKey]?.trim()
                              ? "border-destructive/50"
                              : ""
                          )}
                        />
                        <div className="flex items-center justify-between">
                          {!isOptional ? (
                            <p className="text-xs text-muted-foreground">* Required</p>
                          ) : (
                            <span />
                          )}
                          <p className={cn(
                            "text-xs",
                            (fieldNotes[fieldKey]?.length || 0) >= 80
                              ? "text-destructive"
                              : "text-muted-foreground"
                          )}>
                            {fieldNotes[fieldKey]?.length || 0}/80
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="space-y-6 pt-6">
            <div className="flex items-start gap-4 p-5 rounded-lg bg-muted/20">
              <input
                type="checkbox"
                id="acceptOverride"
                checked={acceptOverride}
                onChange={(e) => setAcceptOverride(e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-2 border-border bg-background text-primary focus:ring-0 focus:ring-offset-0 focus:outline-none cursor-pointer accent-primary"
              />
              <Label
                htmlFor="acceptOverride"
                className="text-sm leading-relaxed cursor-pointer flex-1 text-foreground/90 pt-0.5"
              >
                {criteriaMessage.checks && criteriaMessage.checks.some((c: any) => c.isModified)
                  ? "I accept and agree to update the loan application data."
                  : "I accept and agree to override the credit policy alert."}
              </Label>
            </div>

            <DialogFooter className="flex-row justify-end gap-3 sm:gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowCriteriaModal(false);
                  setAcceptOverride(false);
                  setFieldNotes({});
                }}
                className="px-6 py-2 border border-border text-foreground font-semibold min-w-[100px] hover:border-primary"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  !acceptOverride ||
                  (criteriaMessage.checks ?? []).some((c: any) => {
                    if (!c.isModified || !c.fieldKey) return false;
                    return !LEASEWORKS_OPTIONAL_FIELDS.has(c.fieldKey) && !fieldNotes[c.fieldKey]?.trim();
                  })
                }
                onClick={async () => {
                  const remaining = failedRules.slice(1);
                  if (remaining.length > 0) {
                    setFailedRules(remaining);
                    setCriteriaMessage(remaining[0]);
                    setAcceptOverride(false);
                  } else {
                    setShowCriteriaModal(false);
                    setAcceptOverride(false);
                    const lockOk = await acquireLoanLock(String(inputs.applicationNumber));
                    if (!lockOk) {
                      setCurrentStep("Loans");
                      return;
                    }
                    try {
                      await axios.post(
                        `${serverConfig.api_base_url}underwriting-engine/save-application`,
                        {
                          applicationNumber: inputs.applicationNumber,
                          formData: encodeSentinels(inputs),
                          notes: fieldNotes,
                          actor: UserService.getUsername() || UserService.getEmail() || null,
                          changedFields: modifiedFields,
                        },
                        { headers: { "Content-Type": "application/json" } }
                      );
                    } catch (err) {
                      console.error("Failed to save application fields:", err);
                    }
                    setFieldNotes({});
                    // Reset baseline so coming back to Application shows no diffs
                    setInitialValues(inputs);
                    setModifiedFields({});
                    // Mark criteria as already checked to prevent re-checking
                    setCriteriaChecked(true);
                    // Proceed to Score after accepting override
                    setCurrentStep("Score");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
                className="px-6 py-2 bg-primary text-primary-foreground font-semibold min-w-[150px] hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {failedRules.length > 1 ? "Next" : "Override & Continue"}
              </button>
            </DialogFooter>
          </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Score step — intangibles / override reason modal */}
      <Dialog open={showScoreNotesModal} onOpenChange={(open) => {
        if (!open) {
          setShowScoreNotesModal(false);
          setScoreNotes({});
          setScoreNeedsReason([]);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden p-0 !border-0 flex flex-col [&>button]:h-6 [&>button]:w-6 [&>button]:text-foreground/70 [&>button]:hover:text-foreground [&>button>svg]:h-5 [&>button>svg]:w-5 [&>button]:z-10">
          {/* Sticky header */}
          <div className="shrink-0 flex items-center gap-3 px-8 pt-8 pb-4 bg-background border-b border-border/30">
            <AlertTriangle className="h-6 w-6 text-warning shrink-0" />
            <div>
              <DialogTitle className="text-xl label-athena !text-primary leading-tight">
                Score Override Alert
              </DialogTitle>
              <DialogDescription className="text-sm font-medium text-foreground/70 mt-1 mb-0">
                Extra points were allocated. Provide a mandatory reason for each item before proceeding to Review.
              </DialogDescription>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-8 pb-8 pt-6">
            <div className="space-y-4 py-2">
              {scoreNeedsReason.map((f) => (
                <div key={f.key} className="rounded-lg overflow-hidden bg-warning/5">
                  <div className="flex items-center justify-between p-5">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-warning/15 text-warning">
                        <AlertTriangle className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-base text-foreground mb-1">
                          {f.label}
                        </div>
                        <div className="text-xs text-muted-foreground/60 mt-0.5">
                          Points changed
                        </div>
                      </div>
                    </div>
                    <div className="ml-4 px-4 py-2 rounded-md font-medium text-sm shrink-0 bg-warning/10 text-warning text-right">
                      {String(f.prevVal)} → {String(f.newVal)}
                    </div>
                  </div>
                  <div className="px-5 pb-4 space-y-1">
                    <Textarea
                      placeholder="Reason for change (required)"
                      value={scoreNotes[f.key] ?? ""}
                      onChange={(e) =>
                        setScoreNotes((prev) => ({
                          ...prev,
                          [f.key]: e.target.value.slice(0, 80),
                        }))
                      }
                      maxLength={80}
                      rows={2}
                      className={cn(
                        "text-white placeholder:text-muted-foreground/60",
                        !scoreNotes[f.key]?.trim() ? "border-destructive/50" : ""
                      )}
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">* Required</p>
                      <p className={cn(
                        "text-xs",
                        (scoreNotes[f.key]?.length ?? 0) >= 80
                          ? "text-destructive"
                          : "text-muted-foreground"
                      )}>
                        {scoreNotes[f.key]?.length ?? 0}/80
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-6">
              <DialogFooter className="flex-row justify-end gap-3 sm:gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleScoreNotesAccept}
                  disabled={scoreNeedsReason.some((f) => !scoreNotes[f.key]?.trim())}
                  className="px-6 py-2 bg-primary text-primary-foreground font-semibold min-w-[150px] hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Accept & Continue to Review
                </button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Exposure acknowledgment modal */}
      <Dialog open={showExposureModal} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-lg !border-0 [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <div className="px-8 pt-8 pb-4 border-b border-border/30 flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-warning shrink-0" />
            <div>
              <DialogTitle className="text-xl label-athena !text-primary leading-tight">
                Customer Exposure Alert
              </DialogTitle>
              <DialogDescription className="text-sm font-medium text-foreground/70 mt-1">
                You must acknowledge customer exposure before scoring this deal.
              </DialogDescription>
            </div>
          </div>

          <div className="px-8 py-6 space-y-5">
            <div className="flex items-center justify-between rounded-lg border border-border/40 bg-muted/30 px-5 py-3">
              <span className="text-sm font-medium">Customer Exposure</span>
              <span className="rounded bg-warning/10 px-3 py-1 text-xs font-semibold text-warning">
                {inputs.customerExposure != null
                  ? inputs.customerExposure.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : "$0.00"}
              </span>
            </div>

            <div className="flex items-start gap-4 p-5 rounded-lg bg-muted/20">
              <input
                type="checkbox"
                id="exposureAck"
                checked={exposureChecked}
                onChange={(e) => setExposureChecked(e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-2 border-border bg-background text-primary focus:ring-0 focus:ring-offset-0 focus:outline-none cursor-pointer accent-primary"
              />
              <Label
                htmlFor="exposureAck"
                className="text-sm leading-relaxed cursor-pointer flex-1 text-foreground/90 pt-0.5"
              >
                Confirm and acknowledge you have reviewed customer exposure on this deal.
              </Label>
            </div>

            <DialogFooter className="flex-row justify-end gap-3 sm:gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowExposureModal(false);
                  setExposureChecked(false);
                }}
                className="px-6 py-2 border border-border text-foreground font-semibold min-w-[100px] hover:border-primary"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!exposureChecked}
                onClick={handleExposureAccept}
                className="px-6 py-2 bg-primary text-primary-foreground font-semibold min-w-[100px] hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Accept
              </button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Session taken over — renders above every other modal */}
      <Dialog open={showSessionTakenModal} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm [&>button]:hidden"
          style={{ zIndex: 99999 }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertOctagon className="h-5 w-5 text-destructive" />
              Session Ended
            </DialogTitle>
            <DialogDescription>{sessionTakenMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              className="px-6 py-2 bg-primary text-primary-foreground font-semibold hover:bg-primary/90"
              onClick={() => {
                setShowSessionTakenModal(false);
                setCurrentStep("Loans");
              }}
            >
              OK
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoanCalculate;

